<template>
  <section class="discover-page" @touchstart="handleTouchStart" @touchend="handleTouchEnd">
    <div class="discover-navigation">
      <button
        type="button"
        class="discover-channel-arrow previous"
        :disabled="!canSwitchPreviousChannel"
        :aria-label="$t('discoverDetail.previousChannel')"
        data-analytics="discover.channel_previous"
        @click="stepChannel(-1)"
      ><i class="el-icon-arrow-left" /></button>
      <nav
        ref="channelTabs"
        class="discover-tabs"
        :aria-label="$t('a11y.discoverChannels')"
        @touchstart.stop
        @touchmove.stop
        @touchend.stop
      >
        <button
          v-for="channel in channels"
          :key="channel.code"
          :data-channel="channel.code"
          :class="{ active: activeChannel === channel.code }"
          :data-analytics="`discover.channel.${channel.code}`"
          @click="switchChannel(channel.code)"
        >{{ channelLabel(channel) }}</button>
      </nav>
      <button
        type="button"
        class="discover-channel-arrow next"
        :disabled="!canSwitchNextChannel"
        :aria-label="$t('discoverDetail.nextChannel')"
        data-analytics="discover.channel_next"
        @click="stepChannel(1)"
      ><i class="el-icon-arrow-right" /></button>
      <button class="discover-search" :aria-label="$t('a11y.searchDiscover')" data-analytics="discover.search" @click="openSearch"><i class="el-icon-search" /></button>
    </div>

    <!-- 音频实例不随歌词层收起而销毁，保证迷你播放器控制的是同一条播放流。 -->
    <audio
      v-if="playingMusic"
      ref="musicPlayer"
      :src="playingMusic.audio_url"
      autoplay
      preload="metadata"
      @loadedmetadata="restoreMusicPosition"
      @timeupdate="handleMusicTimeUpdate"
      @play="handleMusicPlay"
      @pause="handleMusicPause"
      @ended="handleMusicEnded"
      @error="handleMusicError"
    />

    <div ref="scroll" class="discover-scroll" @scroll.passive="handleScroll">
      <div v-if="loading && !hasCurrentData" class="discover-skeleton" :aria-label="$t('a11y.loading')">
        <i class="skeleton-hero" />
        <div class="skeleton-shortcuts"><i v-for="index in 4" :key="index" /></div>
        <i v-for="index in 5" :key="`row-${index}`" class="skeleton-row" />
      </div>

      <div v-else-if="pageError && !hasCurrentData" class="discover-state">
        <i class="el-icon-warning-outline" />
        <strong>{{ $t("discover.loadFailed") }}</strong>
        <p>{{ $t("discoverDetail.networkTip") }}</p>
        <button data-analytics="discover.retry" @click="refreshCurrent">{{ $t("common.retry") }}</button>
      </div>

      <template v-else-if="activeChannel === 'featured'">
        <section v-if="featured.banners && featured.banners.length" class="discover-hero">
          <button class="hero-card" :class="currentBanner.theme" data-analytics="discover.banner.open" @click="openBanner(currentBanner)">
            <img :src="currentBanner.image_url" :alt="currentBanner.title" loading="eager" @error="hideBrokenImage">
            <span class="hero-shade" />
            <span class="hero-badge">{{ badgeLabel(currentBanner.badge) }}</span>
            <div>
              <small>{{ bannerTypeLabel(currentBanner.content_type) }}</small>
              <h2>{{ currentBanner.title }}</h2>
              <p>{{ currentBanner.subtitle }}</p>
              <em>{{ currentBanner.button_text }} <i class="el-icon-right" /></em>
            </div>
          </button>
          <div class="hero-dots">
            <button
              v-for="(banner, index) in featured.banners"
              :key="banner.id"
              :class="{ active: index === bannerIndex }"
              :aria-label="$t('a11y.banner', { index: index + 1 })"
              data-analytics="discover.banner.select"
              @click="bannerIndex = index"
            />
          </div>
        </section>

        <section v-if="featured.shortcuts && featured.shortcuts.length" class="discover-shortcuts">
          <button v-for="item in featured.shortcuts" :key="item.code" :data-analytics="`discover.quick.${item.code}`" @click="openShortcut(item)">
            <i :class="item.icon" /><span>{{ quickEntryLabel(item) }}</span><em v-if="item.badge">{{ badgeLabel(item.badge) }}</em>
          </button>
        </section>

        <section v-if="continueItems.length" class="discover-module">
          <module-head :title="$t('discover.continueExperience')" :action="$t('search.viewAll')" @more="openLibrary('history')" />
          <div class="continue-rail">
            <button v-for="item in continueItems" :key="`${item.content_type}-${item.id}`" :data-analytics="`discover.continue.${item.content_type}`" @click="continueContent(item)">
              <img :src="item.cover_url" :alt="item.title" loading="lazy" @error="hideBrokenImage">
              <span><small>{{ continueActionLabel(item) }}</small><strong>{{ item.title }}</strong><em>{{ continueProgress(item) }}</em><i><b :style="{ width: `${item.percent || 0}%` }" /></i></span>
            </button>
          </div>
        </section>

        <section v-if="featured.hot_dramas && featured.hot_dramas.length" class="discover-module">
          <module-head :title="$t('discover.hotDramas')" :action="$t('feed.more')" @more="switchChannel('drama')" />
          <div class="poster-rail">
            <button v-for="drama in featured.hot_dramas" :key="drama.id" data-analytics="discover.content.drama" @click="openDrama(drama.id)">
              <span class="poster"><img :src="drama.cover_url" :alt="drama.title" loading="lazy" @error="hideBrokenImage"><i class="el-icon-video-play" /><em>{{ badgeLabel(drama.badge) }}</em><b v-if="drama.progress" :style="{ width: `${drama.progress.percent}%` }" /></span>
              <strong>{{ drama.title }}</strong><small>{{ drama.serial_status === 'finished' ? $t('discoverUi.finished') : $t('discoverUi.updateTo', { count: drama.updated_episodes }) }} · {{ $t('discoverUi.heat', { value: formatHeat(drama.heat) }) }}</small>
            </button>
          </div>
        </section>

        <section v-if="featured.hot_novels && featured.hot_novels.length" class="discover-module">
          <module-head :title="$t('discover.hotNovels')" :action="$t('feed.more')" @more="switchChannel('novel')" />
          <div class="novel-list featured-novels">
            <novel-card v-for="novel in featured.hot_novels" :key="novel.id" :novel="novel" @open="openNovel" @collect="toggleBookshelf" />
          </div>
        </section>

        <section v-if="featured.hot_music && featured.hot_music.length" class="discover-module">
          <module-head :title="$t('discover.hotMusic')" :action="$t('feed.more')" @more="switchChannel('music')" />
          <div class="music-rail">
            <button v-for="track in featured.hot_music" :key="track.id" data-analytics="discover.content.music" @click="openMusic(track)">
              <span><img :src="track.cover_url" :alt="track.title" loading="lazy" @error="hideBrokenImage"><i class="el-icon-video-play" /><em>{{ badgeLabel(track.badge) }}</em></span>
              <strong>{{ track.title }}</strong>
              <small>{{ track.artist }}</small>
            </button>
          </div>
        </section>

        <section v-if="featured.hot_games && featured.hot_games.length" class="discover-module">
          <module-head :title="$t('discover.hotGames')" :action="$t('feed.more')" @more="switchChannel('game')" />
          <div class="game-rail">
            <button v-for="game in featured.hot_games" :key="game.id" data-analytics="discover.content.game" @click="openGame(game)">
              <span><img :src="game.cover_url" :alt="game.title" loading="lazy" @error="hideBrokenImage"><i class="el-icon-video-play" /><em>{{ badgeLabel(game.badge) }}</em></span>
              <strong>{{ game.title }}</strong>
              <small>{{ $t("gameUi.playCount", { count: formatHeat(game.play_count) }) }}</small>
            </button>
          </div>
        </section>

        <section v-if="featured.rankings && featured.rankings.length" class="discover-module">
          <module-head :title="$t('discover.rankings')" />
          <div class="ranking-grid">
            <button v-for="ranking in featured.rankings" :key="ranking.code" :data-analytics="`discover.ranking.${ranking.code}`" @click="openRanking(ranking.code)">
              <small>TOP CHART</small><strong>{{ rankingLabel(ranking.code) }}</strong>
              <span v-for="(name, index) in ranking.items" :key="name"><b>0{{ index + 1 }}</b>{{ name }}</span>
              <em>{{ $t("discoverUi.fullRanking") }} <i class="el-icon-right" /></em>
            </button>
          </div>
        </section>

        <section v-if="recommendItems.length" class="discover-module recommend-module">
          <module-head :title="$t('discover.forYou')" />
          <article v-for="(item, index) in recommendItems" :key="`${item.content_type}-${item.id}`" class="mixed-card">
            <button class="mixed-main" :data-analytics="`discover.content.${item.content_type}`" @click="openMixed(item)">
              <img :src="item.cover_url" :alt="item.title" loading="lazy" @error="hideBrokenImage">
              <span><small>{{ mixedTypeLabel(item) }}</small><strong>{{ item.title }}</strong><p>{{ item.description }}</p><em>{{ item.category_name }} · {{ $t('discoverUi.heat', { value: formatHeat(item.heat) }) }}</em></span>
            </button>
            <button class="mixed-more" :aria-label="$t('a11y.moreActions')" data-analytics="discover.not_interested" @click="markNotInterested(item, index)"><i class="el-icon-more" /></button>
          </article>
        </section>
      </template>

      <template v-else-if="activeChannel === 'drama'">
        <nav class="category-rail">
          <button v-for="item in dramaCategories" :key="item.code" :class="{ active: dramaCategory === item.code }" :data-analytics="`drama.category.${item.code}`" @click="changeDramaCategory(item.code)">{{ categoryLabel('drama', item) }}</button>
        </nav>
        <section v-if="dramaList.length" class="channel-feature">
          <img :src="dramaList[0].banner_url || dramaList[0].cover_url" :alt="dramaList[0].title" @error="hideBrokenImage">
          <span class="channel-feature-shade" />
          <div><small>EDITOR'S PICK</small><h2>{{ dramaList[0].title }}</h2><p>{{ dramaList[0].description }}</p><em>{{ dramaList[0].category_name }} · {{ badgeLabel(dramaList[0].badge) }}</em><button @click="openDrama(dramaList[0].id, true)"><i class="el-icon-video-play" /> {{ $t("discover.watchNow") }}</button></div>
        </section>
        <section v-if="dramaList.length" class="discover-module drama-channel-module">
          <module-head :title="dramaCategoryName" />
          <div class="drama-grid">
            <button v-for="drama in dramaList" :key="drama.id" data-analytics="drama.open" @click="openDrama(drama.id)">
              <span><img :src="drama.cover_url" :alt="drama.title" loading="lazy" @error="hideBrokenImage"><em>{{ badgeLabel(drama.badge) }}</em><i class="el-icon-video-play" /><b v-if="drama.progress" :style="{ width: `${drama.progress.percent}%` }" /></span>
              <strong>{{ drama.title }}</strong>
              <small>{{ drama.serial_status === 'finished' ? $t('discoverUi.finished') : $t('discoverUi.episodesCount', { current: drama.updated_episodes, total: drama.total_episodes }) }} · {{ $t('discoverUi.heat', { value: formatHeat(drama.heat) }) }}</small>
            </button>
          </div>
        </section>
        <empty-state v-else :text="$t('discoverUi.emptyDrama')" />
        <p v-if="listLoading" class="list-status"><i class="el-icon-loading" /> {{ $t("common.loading") }}</p>
        <p v-else-if="!dramaHasMore && dramaList.length" class="list-status">{{ $t("discoverUi.end") }}</p>
      </template>

      <template v-else-if="activeChannel === 'novel'">
        <nav class="category-rail">
          <button v-for="item in novelCategories" :key="item.code" :class="{ active: novelCategory === item.code }" :data-analytics="`novel.category.${item.code}`" @click="changeNovelCategory(item.code)">{{ categoryLabel('novel', item) }}</button>
        </nav>
        <section v-if="novelList.length" class="novel-feature">
          <img :src="novelList[0].cover_url" :alt="novelList[0].title" @error="hideBrokenImage">
          <div><small>THIS WEEK'S BOOK</small><h2>{{ novelList[0].title }}</h2><em>{{ novelList[0].author }}</em><p>{{ novelList[0].description }}</p><span>{{ novelList[0].category_name }} · {{ formatHeat(novelList[0].read_count) }}</span><button @click="openNovel(novelList[0].id, true)">{{ $t("discover.readNow") }}</button></div>
        </section>
        <div class="novel-rank-actions">
          <button @click="openRanking('novel_hot')">{{ $t("discoverUi.hotRank") }}</button><button @click="openRanking('novel_new')">{{ $t("discoverUi.newRank") }}</button><button @click="openRanking('novel_finished')">{{ $t("discoverUi.finishedRank") }}</button><button @click="openLibrary('bookshelf')">{{ $t("discoverUi.bookshelf") }}</button>
        </div>
        <section v-if="novelList.length" class="discover-module novel-channel-module">
          <module-head :title="novelCategoryName" />
          <div class="novel-list">
            <novel-card v-for="novel in novelList" :key="novel.id" :novel="novel" @open="openNovel" @collect="toggleBookshelf" />
          </div>
        </section>
        <empty-state v-else :text="$t('discoverUi.emptyNovel')" />
        <p v-if="listLoading" class="list-status"><i class="el-icon-loading" /> {{ $t("common.loading") }}</p>
        <p v-else-if="!novelHasMore && novelList.length" class="list-status">{{ $t("discoverUi.end") }}</p>
      </template>

      <template v-else-if="activeChannel === 'music'">
        <nav class="category-rail">
          <button v-for="item in musicCategories" :key="item.code" :class="{ active: musicCategory === item.code }" :data-analytics="`music.category.${item.code}`" @click="changeMusicCategory(item.code)">{{ categoryLabel('music', item) }}</button>
        </nav>
        <section v-if="musicList.length" class="music-feature">
          <img :src="musicList[0].cover_url" :alt="musicList[0].title" @error="hideBrokenImage">
          <span class="music-feature-shade" />
          <div><small>STARLIVE SOUND PICK</small><h2>{{ musicList[0].title }}</h2><strong>{{ musicList[0].artist }}</strong><p>{{ musicList[0].description }}</p><button @click="openMusic(musicList[0])"><i class="el-icon-headset" /> {{ $t("discoverUi.playNow") }}</button></div>
        </section>
        <section v-if="musicList.length" class="discover-module music-channel-module">
          <module-head :title="musicCategoryName" :action="$t('musicUi.myFavorites')" @more="openLibrary('music')" />
          <div class="music-list">
            <article v-for="(track, index) in musicList" :key="track.id" class="music-row">
              <button class="music-main" data-analytics="music.open" @click="openMusic(track)">
                <b>{{ String(index + 1).padStart(2, "0") }}</b>
                <span class="music-cover"><img :src="track.cover_url" :alt="track.title" loading="lazy" @error="hideBrokenImage"><i class="el-icon-video-play" /></span>
                <span class="music-copy"><strong>{{ track.title }}</strong><small>{{ track.artist }}<template v-if="track.album"> · {{ track.album }}</template></small><em>{{ $t("discoverUi.playCount", { count: formatHeat(track.play_count) }) }} · {{ formatPlayerTime(track.duration) }}</em></span>
              </button>
              <button class="music-favorite" :aria-label="$t(track.collected ? 'musicUi.removeFavorite' : 'musicUi.addFavorite')" data-analytics="music.favorite" @click="toggleMusicFavorite(track)"><i :class="track.collected ? 'el-icon-star-on' : 'el-icon-star-off'" /></button>
            </article>
          </div>
        </section>
        <empty-state v-else :text="$t('discoverUi.emptyMusic')" />
        <p v-if="listLoading" class="list-status"><i class="el-icon-loading" /> {{ $t("common.loading") }}</p>
        <p v-else-if="!musicHasMore && musicList.length" class="list-status">{{ $t("discoverUi.end") }}</p>
      </template>

      <template v-else-if="activeChannel === 'game'">
        <nav class="category-rail">
          <button v-for="item in gameCategories" :key="item.code" :class="{ active: gameCategory === item.code }" :data-analytics="`game.category.${item.code}`" @click="changeGameCategory(item.code)">{{ categoryLabel('game', item) }}</button>
        </nav>
        <section v-if="gameList.length" class="game-feature">
          <img :src="gameList[0].banner_url || gameList[0].cover_url" :alt="gameList[0].title" @error="hideBrokenImage">
          <span />
          <div><small>{{ $t("gameUi.featuredLabel") }}</small><h2>{{ gameList[0].title }}</h2><p>{{ gameList[0].description }}</p><em>{{ gameList[0].category_name }} · {{ badgeLabel(gameList[0].badge) }}</em><button @click="openGame(gameList[0])"><i class="el-icon-video-play" />{{ $t("gameUi.playNow") }}</button></div>
        </section>
        <section v-if="gameList.length" class="discover-module game-channel-module">
          <module-head :title="gameCategoryName" :action="$t('gameUi.myFavorites')" @more="openLibrary('game')" />
          <div class="game-grid">
            <article v-for="game in gameList" :key="game.id">
              <button class="game-card-main" data-analytics="game.open" @click="openGame(game)">
                <span><img :src="game.cover_url" :alt="game.title" loading="lazy" @error="hideBrokenImage"><em>{{ badgeLabel(game.badge) }}</em><i class="el-icon-video-play" /></span>
                <strong>{{ game.title }}</strong>
                <small>{{ game.category_name }} · {{ $t("gameUi.playCount", { count: formatHeat(game.play_count) }) }}</small>
              </button>
              <button class="game-card-favorite" :aria-label="$t(game.collected ? 'gameUi.removeFavorite' : 'gameUi.addFavorite')" data-analytics="game.favorite" @click="toggleGameFavorite(game)"><i :class="game.collected ? 'el-icon-star-on' : 'el-icon-star-off'" /></button>
            </article>
          </div>
        </section>
        <empty-state v-else :text="$t('discoverUi.emptyGame')" />
        <p v-if="listLoading" class="list-status"><i class="el-icon-loading" /> {{ $t("common.loading") }}</p>
        <p v-else-if="!gameHasMore && gameList.length" class="list-status">{{ $t("discoverUi.end") }}</p>
      </template>

      <decoration-gallery
        v-else
        ref="decorationShop"
        mode="shop"
        :logged-in="loggedIn"
        :user-avatar="userAvatar"
        @login="$emit('login')"
      />

      <button v-if="hasCurrentData" class="discover-refresh" :disabled="loading" data-analytics="discover.refresh" @click="refreshCurrent"><i :class="loading ? 'el-icon-loading' : 'el-icon-refresh'" /> {{ $t("discoverUi.refresh") }}</button>
    </div>

    <transition name="discover-panel">
      <section v-if="searchVisible" class="discover-overlay search-overlay">
        <header><button :aria-label="$t('a11y.backDiscover')" @click="closeSearch"><i class="el-icon-back" /></button><div><i class="el-icon-search" /><input ref="searchInput" v-model.trim="searchKeyword" :placeholder="$t('discoverDetail.searchPlaceholder')" @keyup.enter="submitSearch"><button v-if="searchKeyword" @click="searchKeyword = ''"><i class="el-icon-close" /></button></div><button @click="submitSearch">{{ $t("nav.search") }}</button></header>
        <nav v-if="searchSubmitted" class="search-type-tabs"><button v-for="item in searchTypes" :key="item.code" :class="{ active: searchType === item.code }" @click="changeSearchType(item.code)">{{ $t(`discoverUi.${item.code}`) }}</button></nav>
        <div class="overlay-scroll">
          <template v-if="!searchSubmitted">
            <section v-if="searchHistory.length" class="search-block"><div><h3>{{ $t("discover.recentSearch") }}</h3><button @click="clearSearchHistory">{{ $t("search.clearAll") }}</button></div><span class="keyword-cloud"><button v-for="word in searchHistory" :key="word" @click="searchByWord(word)">{{ word }} <i class="el-icon-close" @click.stop="removeSearchHistory(word)" /></button></span></section>
            <section class="search-block"><div><h3>{{ $t("discover.hotSearch") }}</h3></div><ol class="hot-keywords"><li v-for="(word, index) in hotKeywords" :key="word"><button @click="searchByWord(word)"><b>{{ index + 1 }}</b><span>{{ word }}</span><em v-if="index < 3">{{ $t(index === 0 ? "discoverDetail.hotBadge" : "discoverDetail.trendingBadge") }}</em></button></li></ol></section>
          </template>
          <div v-else-if="searchLoading" class="discover-state compact"><i class="el-icon-loading" /><strong>{{ $t("discover.searching") }}</strong></div>
          <template v-else-if="searchResults.dramas.length || searchResults.novels.length || searchResults.music.length || searchResults.games.length">
            <section v-if="searchResults.dramas.length" class="search-result-block"><h3>{{ $t("discover.dramas") }} <small>{{ $t("discover.results", { count: searchResults.dramas.length }) }}</small></h3><div class="drama-grid"><button v-for="item in searchResults.dramas" :key="item.id" @click="openDrama(item.id)"><span><img :src="item.cover_url" :alt="item.title"><em>{{ badgeLabel(item.badge) }}</em><i class="el-icon-video-play" /></span><strong>{{ item.title }}</strong><small>{{ item.category_name }} · {{ formatHeat(item.heat) }}</small></button></div></section>
            <section v-if="searchResults.novels.length" class="search-result-block"><h3>{{ $t("discover.novels") }} <small>{{ $t("discover.results", { count: searchResults.novels.length }) }}</small></h3><div class="novel-list"><novel-card v-for="item in searchResults.novels" :key="item.id" :novel="item" @open="openNovel" @collect="toggleBookshelf" /></div></section>
            <section v-if="searchResults.music.length" class="search-result-block"><h3>{{ $t("discover.music") }} <small>{{ $t("discover.results", { count: searchResults.music.length }) }}</small></h3><div class="music-list"><article v-for="item in searchResults.music" :key="item.id" class="music-row"><button class="music-main" @click="openMusic(item)"><span class="music-cover"><img :src="item.cover_url" :alt="item.title"><i class="el-icon-video-play" /></span><span class="music-copy"><strong>{{ item.title }}</strong><small>{{ item.artist }} · {{ item.album }}</small></span></button></article></div></section>
            <section v-if="searchResults.games.length" class="search-result-block"><h3>{{ $t("discover.games") }} <small>{{ $t("discover.results", { count: searchResults.games.length }) }}</small></h3><div class="game-grid search-game-grid"><article v-for="item in searchResults.games" :key="item.id"><button class="game-card-main" @click="openGame(item)"><span><img :src="item.cover_url" :alt="item.title"><i class="el-icon-video-play" /></span><strong>{{ item.title }}</strong><small>{{ item.category_name }}</small></button></article></div></section>
          </template>
          <empty-state v-else :text="$t('discoverDetail.noResults')" :description="$t('discoverDetail.tryKeyword')" />
        </div>
      </section>
    </transition>

    <transition name="discover-panel">
      <section v-if="libraryVisible" class="discover-overlay library-overlay">
        <header class="overlay-header"><button :aria-label="$t('a11y.backDiscover')" @click="libraryVisible = false"><i class="el-icon-back" /></button><strong>{{ $t("discoverLibrary.title") }}</strong><button v-if="libraryTab === 'history' && libraryHistory.length" :aria-label="$t('discoverLibrary.clearAll')" @click="clearLibraryHistory"><i class="el-icon-delete" /></button><span v-else /></header>
        <nav class="library-tabs">
          <button :class="{ active: libraryTab === 'history' }" @click="switchLibraryTab('history')">{{ $t("discover.continueExperience") }} <small>{{ $n(libraryHistoryTotal) }}</small></button>
          <button :class="{ active: libraryTab === 'bookshelf' }" @click="switchLibraryTab('bookshelf')">{{ $t("discoverUi.bookshelf") }} <small>{{ $n(libraryShelfTotal) }}</small></button>
          <button :class="{ active: libraryTab === 'music' }" @click="switchLibraryTab('music')">{{ $t("musicUi.myFavorites") }} <small>{{ $n(libraryMusicTotal) }}</small></button>
          <button :class="{ active: libraryTab === 'game' }" @click="switchLibraryTab('game')">{{ $t("gameUi.myFavorites") }} <small>{{ $n(libraryGameTotal) }}</small></button>
        </nav>
        <div class="overlay-scroll library-scroll">
          <div v-if="libraryLoading && !activeLibraryItems.length" class="discover-state compact"><i class="el-icon-loading" /><strong>{{ $t("common.loading") }}</strong></div>
          <template v-else-if="libraryTab === 'history' && libraryHistory.length">
            <article v-for="item in libraryHistory" :key="`${item.content_type}-${item.id}`" class="library-row">
              <button class="library-main" @click="continueContent(item)"><span class="library-cover"><img :src="item.cover_url" :alt="item.title" @error="hideBrokenImage"><i :class="item.content_type === 'game' ? 'el-icon-magic-stick' : item.content_type === 'music' ? 'el-icon-headset' : item.content_type === 'drama' ? 'el-icon-video-play' : 'el-icon-reading'" /></span><span class="library-copy"><small>{{ continueTypeLabel(item) }}</small><strong>{{ item.title }}</strong><em>{{ continueProgress(item) }}</em><i><b :style="{ width: `${item.percent || 0}%` }" /></i><span>{{ continueActionLabel(item) }} <i class="el-icon-right" /></span></span></button>
              <button class="library-remove" :aria-label="$t('discoverLibrary.deleteRecord')" @click="deleteLibraryHistory(item)"><i class="el-icon-delete" /></button>
            </article>
          </template>
          <template v-else-if="libraryTab === 'bookshelf' && libraryShelf.length">
            <article v-for="novel in libraryShelf" :key="novel.id" class="library-row shelf-row">
              <button class="library-main" @click="openNovel(novel.id)"><span class="library-cover"><img :src="novel.cover_url" :alt="novel.title" @error="hideBrokenImage"><i class="el-icon-reading" /></span><span class="library-copy"><small>{{ novel.author }}</small><strong>{{ novel.title }}</strong><em>{{ novel.progress ? $t("discoverUi.progressChapter", { count: novel.progress.chapter_no }) : $t("discoverLibrary.notStarted") }}</em><i v-if="novel.progress"><b :style="{ width: `${novel.progress.position_percent || 0}%` }" /></i></span></button>
              <button class="library-continue" @click="openNovel(novel.id, true)">{{ $t(novel.progress ? "discoverUi.continueRead" : "discoverDetail.startReading") }} <i class="el-icon-right" /></button>
              <button class="library-remove" :aria-label="$t('a11y.removeBookshelf')" @click="removeLibraryShelf(novel)"><i class="el-icon-star-on" /></button>
            </article>
          </template>
          <template v-else-if="libraryTab === 'music' && libraryMusic.length">
            <article v-for="track in libraryMusic" :key="track.id" class="library-row favorite-row">
              <button class="library-main" @click="openMusic(track)"><span class="library-cover"><img :src="track.cover_url" :alt="track.title" @error="hideBrokenImage"><i class="el-icon-headset" /></span><span class="library-copy"><small>{{ track.artist }}</small><strong>{{ track.title }}</strong><em>{{ $t("discoverUi.playCount", { count: formatHeat(track.play_count) }) }} · {{ formatPlayerTime(track.duration) }}</em></span></button>
              <button class="library-continue" @click="openMusic(track)">{{ $t(track.progress ? "discoverUi.continueListen" : "discoverUi.playNow") }} <i class="el-icon-right" /></button>
              <button class="library-remove" :aria-label="$t('musicUi.removeFavorite')" @click="toggleMusicFavorite(track)"><i class="el-icon-star-on" /></button>
            </article>
          </template>
          <template v-else-if="libraryTab === 'game' && libraryGame.length">
            <article v-for="game in libraryGame" :key="game.id" class="library-row favorite-row">
              <button class="library-main" @click="openGame(game)"><span class="library-cover"><img :src="game.cover_url" :alt="game.title" @error="hideBrokenImage"><i class="el-icon-magic-stick" /></span><span class="library-copy"><small>{{ game.category_name }}</small><strong>{{ game.title }}</strong><em>{{ $t("gameUi.playCount", { count: formatHeat(game.play_count) }) }}</em></span></button>
              <button class="library-continue" @click="openGame(game)">{{ $t(game.progress ? "discoverUi.continuePlay" : "gameUi.playNow") }} <i class="el-icon-right" /></button>
              <button class="library-remove" :aria-label="$t('gameUi.removeFavorite')" @click="toggleGameFavorite(game)"><i class="el-icon-star-on" /></button>
            </article>
          </template>
          <empty-state v-else-if="!libraryLoading" :text="$t(libraryEmptyKey)" :description="$t(`${libraryEmptyKey}Hint`)" />
          <button v-if="activeLibraryHasMore" class="library-load-more" :disabled="libraryLoading" @click="loadMoreLibrary">{{ $t(libraryLoading ? "appUi.loadingMore" : "discoverLibrary.loadMore") }}</button>
        </div>
      </section>
    </transition>

    <transition name="discover-panel">
      <section v-if="dramaDetailVisible && dramaDetail" class="discover-overlay detail-overlay">
        <header class="overlay-header"><button :aria-label="$t('a11y.backDiscover')" @click="closeDramaDetail"><i class="el-icon-back" /></button><strong>{{ $t("discover.dramaDetail") }}</strong><button :aria-label="$t('a11y.share')" @click="shareDiscover('drama', dramaDetail.drama)"><i class="el-icon-share" /></button></header>
        <div class="overlay-scroll">
          <div class="drama-detail-hero">
            <img :src="dramaDetail.drama.banner_url || dramaDetail.drama.cover_url" :alt="dramaDetail.drama.title">
            <span />
            <div><em>{{ badgeLabel(dramaDetail.drama.badge) }}</em><h1>{{ dramaDetail.drama.title }}</h1><p>{{ dramaDetail.drama.description }}</p><small>{{ dramaDetail.drama.category_name }} · {{ dramaDetail.drama.actors }}</small><button @click="playDrama()"><i class="el-icon-video-play" /> {{ $t(dramaDetail.drama.progress ? "discoverUi.continueWatch" : "discoverDetail.playFirst") }}</button></div>
          </div>
          <section class="detail-section"><div class="detail-title"><h3>{{ $t("discover.episodes") }}</h3><span>{{ playableEpisodeCount }}/{{ playableEpisodeCount }}</span></div><div class="episode-grid"><button v-for="episode in dramaDetail.episodes" :key="episode.id" :class="{ watched: dramaDetail.drama.progress && episode.episode_no <= dramaDetail.drama.progress.episode_no }" @click="playDrama(episode)"><strong>{{ episode.episode_no }}</strong><i v-if="!episode.is_free" class="el-icon-lock" /><small v-else>{{ episode.duration }}s</small></button></div></section>
          <section v-if="dramaDetail.recommend_list.length" class="detail-section"><h3>{{ $t("discover.related") }}</h3><div class="poster-rail compact-posters"><button v-for="item in dramaDetail.recommend_list" :key="item.id" @click="openDrama(item.id)"><span class="poster"><img :src="item.cover_url" :alt="item.title"><em>{{ badgeLabel(item.badge) }}</em></span><strong>{{ item.title }}</strong><small>{{ item.updated_episodes }}/{{ item.total_episodes }}</small></button></div></section>
        </div>
      </section>
    </transition>

    <transition name="discover-fade">
      <section v-if="playerVisible && playingEpisode" class="drama-player">
        <video ref="dramaPlayer" :src="playingEpisode.video_url" data-analytics="drama.play_toggle" playsinline webkit-playsinline autoplay @loadedmetadata="restoreDramaPosition" @timeupdate="handleDramaTimeUpdate" @play="dramaPlaying = true" @pause="dramaPlaying = false" @ended="playNextEpisode" @click="toggleDramaPlayback" />
        <span class="player-shade" />
        <header><button :aria-label="$t('a11y.closePlayer')" @click="closePlayer"><i class="el-icon-close" /></button><div><small>{{ playingDrama.title }}</small><strong>{{ $t("discover.episodeTitle", { no: playingEpisode.episode_no, title: playingEpisode.title }) }}</strong></div><button :aria-label="$t(dramaMuted ? 'feed.muteOn' : 'feed.muteOff')" @click="toggleDramaMute"><i :class="dramaMuted ? 'el-icon-turn-off-microphone' : 'el-icon-microphone'" /></button></header>
        <button v-if="!dramaPlaying" class="player-play" :aria-label="$t('player.play')" data-analytics="drama.play_toggle" @click="toggleDramaPlayback"><i class="el-icon-video-play" /></button>
        <div class="player-bottom">
          <div class="player-progress">
            <span>{{ formatPlayerTime(dramaCurrentTime) }}</span>
            <input :aria-label="$t('player.progress')" type="range" min="0" :max="Math.max(1, dramaDuration)" step="0.1" :value="dramaSeeking ? dramaSeekTime : dramaCurrentTime" :style="{ '--player-progress': `${dramaDuration ? (dramaSeeking ? dramaSeekTime : dramaCurrentTime) / dramaDuration * 100 : 0}%` }" @pointerdown="beginDramaSeek" @touchstart="beginDramaSeek" @input="seekDrama" @change="finishDramaSeek" @pointerup="finishDramaSeek" @touchend="finishDramaSeek">
            <span>{{ formatPlayerTime(dramaDuration) }}</span>
          </div>
          <footer><button :disabled="!playingPrevious" data-analytics="drama.previous_episode" @click="playAdjacentEpisode(playingPrevious)">{{ $t("discoverDetail.previousEpisode") }}</button><span>{{ playingEpisode.episode_no }} / {{ playableEpisodeCount }}</span><button :disabled="!playingNext || liveTransitionLoading" data-analytics="drama.next_episode" @click="requestNextEpisode"><i v-if="liveTransitionLoading" class="el-icon-loading" />{{ $t("discoverDetail.nextEpisode") }}</button></footer>
        </div>
      </section>
    </transition>

    <transition name="discover-panel">
      <section v-if="novelDetailVisible && novelDetail" class="discover-overlay novel-detail-overlay">
        <header class="overlay-header"><button :aria-label="$t('a11y.backDiscover')" @click="novelDetailVisible = false"><i class="el-icon-back" /></button><strong>{{ $t("discover.novelDetail") }}</strong><button :aria-label="$t('a11y.share')" @click="shareDiscover('novel', novelDetail.novel)"><i class="el-icon-share" /></button></header>
        <div class="overlay-scroll">
          <section class="book-hero"><img :src="novelDetail.novel.cover_url" :alt="novelDetail.novel.title"><div><em>{{ badgeLabel(novelDetail.novel.badge) }}</em><h1>{{ novelDetail.novel.title }}</h1><strong>{{ novelDetail.novel.author }}</strong><p>{{ novelDetail.novel.category_name }} · {{ $t("discoverDetail.wordCount", { count: formatWords(novelDetail.novel.word_count) }) }} · {{ $t(novelDetail.novel.serial_status === 'finished' ? "discoverUi.finished" : "discoverUi.serializing") }}</p><span>{{ $t("discoverDetail.readerCount", { count: formatHeat(novelDetail.novel.read_count) }) }}</span></div></section>
          <section class="book-description"><h3>{{ $t("discover.intro") }}</h3><p>{{ novelDetail.novel.description }}</p><div><span v-for="tag in novelDetail.novel.tags" :key="tag">{{ tag }}</span></div></section>
          <section class="detail-section chapter-preview"><div class="detail-title"><h3>{{ $t("discover.directory") }}</h3><button @click="openChapterDirectory">{{ novelDetail.novel.chapter_count }} <i class="el-icon-right" /></button></div><p>{{ novelDetail.novel.latest_chapter_name || $t("discoverDetail.noChapters") }}</p></section>
          <section v-if="novelDetail.recommend_list.length" class="detail-section"><h3>{{ $t("discover.readersLike") }}</h3><div class="poster-rail compact-posters"><button v-for="item in novelDetail.recommend_list" :key="item.id" @click="openNovel(item.id)"><span class="poster book-poster"><img :src="item.cover_url" :alt="item.title"><em>{{ badgeLabel(item.badge) }}</em></span><strong>{{ item.title }}</strong><small>{{ item.author }}</small></button></div></section>
        </div>
        <footer class="novel-detail-actions"><button data-analytics="novel.bookshelf_toggle" @click="toggleBookshelf(novelDetail.novel)"><i :class="novelDetail.novel.collected ? 'el-icon-star-on' : 'el-icon-star-off'" />{{ $t(novelDetail.novel.collected ? "discoverDetail.onBookshelf" : "discoverDetail.addBookshelf") }}</button><button :disabled="!novelDetail.first_chapter" data-analytics="novel.read" @click="readNovel()">{{ $t(novelDetail.first_chapter ? (novelDetail.novel.progress ? "discoverUi.continueRead" : "discoverDetail.startReading") : "discoverDetail.noChapters") }}</button></footer>
      </section>
    </transition>

    <transition name="discover-panel">
      <section v-if="directoryVisible" class="discover-overlay directory-overlay">
        <header class="overlay-header"><button :aria-label="$t('a11y.closeDirectory')" @click="directoryVisible = false"><i class="el-icon-back" /></button><strong>{{ $t("discover.chapterDirectory") }}</strong><button @click="toggleChapterOrder">{{ chapterOrder === 'asc' ? $t("discover.ascending") : $t("discover.descending") }}</button></header>
        <div class="overlay-scroll"><button v-for="chapter in chapterList" :key="chapter.id" :class="['chapter-row', { active: readerData && Number(readerData.chapter.id) === Number(chapter.id) }]" @click="openReader(chapter.id)"><span><small>{{ $t("discoverDetail.chapterNo", { no: chapter.chapter_no }) }}</small><strong>{{ chapter.title }}</strong></span><i :class="chapter.audio_available ? 'el-icon-headset' : chapter.is_free ? 'el-icon-arrow-right' : 'el-icon-lock'" /></button><empty-state v-if="!chapterLoading && !chapterList.length" :text="$t('discoverDetail.noChapters')" /><button v-if="chapterHasMore" class="chapter-load-more" :disabled="chapterLoading" @click="loadMoreChapters">{{ $t(chapterLoading ? "appUi.loadingMore" : "discoverDetail.loadMoreChapters") }}</button></div>
      </section>
    </transition>

    <transition name="discover-fade">
      <section v-if="readerVisible && readerData" :class="['novel-reader', `reader-theme-${readerTheme}`]" :style="readerStyle">
        <audio
          v-if="readerData.chapter.audio_available"
          ref="novelAudioPlayer"
          :src="readerData.chapter.audio_url"
          preload="metadata"
          @loadedmetadata="handleAudiobookLoaded"
          @timeupdate="handleAudiobookTimeUpdate"
          @play="audiobookPlaying = true"
          @pause="audiobookPlaying = false"
          @ended="handleAudiobookEnded"
          @error="handleAudiobookError"
        />
        <header>
          <button :aria-label="$t('a11y.exitReader')" @click="closeReader"><i class="el-icon-back" /></button>
          <div class="reader-heading"><small>{{ readerData.novel.title }}</small><strong>{{ readerData.chapter.title }}</strong></div>
          <div class="reader-actions">
            <button :class="{ active: audiobookVisible }" :disabled="!readerData.chapter.audio_available" :aria-label="$t(readerData.chapter.audio_available ? 'audiobookUi.title' : 'audiobookUi.unavailable')" data-analytics="novel.audiobook_open" @click="toggleAudiobookPanel"><i class="el-icon-headset" /></button>
            <button :class="{ active: readerSettingsVisible }" :aria-label="$t('readerSettings.title')" data-analytics="novel.reader_settings" @click="readerSettingsVisible = !readerSettingsVisible"><i class="el-icon-setting" /></button>
            <button :aria-label="$t('a11y.chapterDirectory')" data-analytics="novel.directory_open" @click="openChapterDirectory"><i class="el-icon-s-operation" /></button>
          </div>
        </header>
        <reader-settings
          :visible="readerSettingsVisible"
          :font-size="readerFontSize"
          :line-height="readerLineHeight"
          :theme="readerTheme"
          @close="readerSettingsVisible = false"
          @font-change="updateReaderPreference('fontSize', $event)"
          @line-height-change="updateReaderPreference('lineHeight', $event)"
          @theme-change="updateReaderPreference('theme', $event)"
        />
        <aside v-if="audiobookVisible" class="audiobook-panel">
          <header><span><small>{{ $t("audiobookUi.eyebrow") }}</small><strong>{{ readerData.chapter.title }}</strong></span><button :aria-label="$t('audiobookUi.close')" @click="audiobookVisible = false"><i class="el-icon-close" /></button></header>
          <input :aria-label="$t('audiobookUi.progress')" type="range" min="0" :max="Math.max(1, audiobookDuration)" step="1" :value="audiobookTime" :style="{ '--audio-progress': `${audiobookDuration ? audiobookTime / audiobookDuration * 100 : 0}%` }" @input="seekAudiobook">
          <div class="audiobook-times"><span>{{ formatPlayerTime(audiobookTime) }}</span><span>{{ formatPlayerTime(audiobookDuration) }}</span></div>
          <div class="audiobook-controls">
            <button :disabled="!readerData.previous || !readerData.previous.audio_available" :aria-label="$t('audiobookUi.previous')" @click="openAudiobookChapter(readerData.previous)"><i class="el-icon-arrow-left" /></button>
            <button class="audiobook-play" :aria-label="$t(audiobookPlaying ? 'audiobookUi.pause' : 'audiobookUi.play')" @click="toggleAudiobook"><i :class="audiobookPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'" /></button>
            <button :disabled="!readerData.next || !readerData.next.audio_available" :aria-label="$t('audiobookUi.next')" @click="openAudiobookChapter(readerData.next)"><i class="el-icon-arrow-right" /></button>
            <button class="audiobook-rate" :aria-label="$t('audiobookUi.speed')" @click="cycleAudiobookRate">{{ audiobookRate }}×</button>
          </div>
        </aside>
        <div ref="readerScroll" class="reader-scroll" @click="readerSettingsVisible = false" @scroll.passive="handleReaderScroll">
          <article><h1>{{ readerData.chapter.title }}</h1><small>{{ readerData.novel.author }} · {{ $t("discoverDetail.wordCount", { count: $n(readerData.chapter.word_count) }) }}</small><div class="reader-content" v-html="readerContentHtml" /></article>
          <footer><button :disabled="!readerData.previous" data-analytics="novel.previous_chapter" @click="openReader(readerData.previous && readerData.previous.id)">{{ $t("discoverDetail.previousChapter") }}</button><button :disabled="!readerData.next || liveTransitionLoading" data-analytics="novel.next_chapter" @click="requestNextChapter"><i v-if="liveTransitionLoading" class="el-icon-loading" />{{ $t("discoverDetail.nextChapter") }}</button></footer>
        </div>
        <span class="reader-progress">{{ readerPercent }}%</span>
      </section>
    </transition>

    <transition name="discover-fade">
      <section v-if="liveTransitionVisible && liveTransitionVideo" class="discover-live-transition">
        <button type="button" class="discover-live-card-close" :aria-label="$t('a11y.closePlayer')" @click="closeLiveTransition"><i class="el-icon-close" /></button>
        <button type="button" class="discover-live-card-main" :aria-label="$t('liveRoom.enter')" @click="openLiveTransitionRoom">
          <img class="discover-live-card-backdrop" :src="liveTransitionVideo.cover_url" :alt="liveTransitionVideo.title" @error="hideBrokenImage">
          <span class="discover-live-card-shade" />
          <span class="discover-live-card-copy">
            <em><i />{{ $t("liveRoom.live") }}</em>
            <span class="discover-live-card-author">
              <platform-avatar v-if="liveTransitionVideo.author" :src="liveTransitionVideo.author.avatar_url" :alt="liveTransitionVideo.author.name" />
              <b v-else>{{ liveTransitionVideo.author && liveTransitionVideo.author.name ? liveTransitionVideo.author.name.slice(0, 1) : "SL" }}</b>
              <span><small>{{ $t("feedDiscovery.recommendation") }}</small><strong>{{ liveTransitionVideo.author && liveTransitionVideo.author.name }}</strong></span>
            </span>
            <h2>{{ liveTransitionVideo.title }}</h2>
            <p>{{ $t("liveRoom.watching", { count: $compact(liveTransitionVideo.stats && liveTransitionVideo.stats.heat || 0) }) }}</p>
            <span class="discover-live-card-enter"><i class="el-icon-video-play" />{{ $t("liveRoom.enter") }}</span>
          </span>
        </button>
        <nav class="discover-live-transition-nav" :aria-label="$t('liveRoom.live')">
          <button type="button" :disabled="!liveTransitionPrevious" @click="goPreviousFromLive"><i class="el-icon-arrow-left" /><span>{{ $t(liveTransitionType === 'drama' ? 'discoverDetail.previousEpisode' : 'discoverDetail.previousChapter') }}</span></button>
          <em><i />{{ $t("liveRoom.live") }}</em>
          <button type="button" :disabled="!liveTransitionTarget" @click="continueLiveTransition"><span>{{ $t(liveTransitionType === 'drama' ? 'discoverDetail.nextEpisode' : 'discoverDetail.nextChapter') }}</span><i class="el-icon-arrow-right" /></button>
        </nav>
      </section>
    </transition>

    <transition name="discover-fade">
      <section v-if="musicPlayerVisible && playingMusic" class="music-player">
        <div class="music-player-backdrop"><img :src="playingMusic.cover_url" alt=""></div>
        <header><button :aria-label="$t('musicUi.collapse')" @click="collapseMusicPlayer"><i class="el-icon-arrow-down" /></button><strong>{{ $t("musicUi.nowPlaying") }}</strong><button :aria-label="$t('a11y.share')" @click="shareDiscover('music', playingMusic)"><i class="el-icon-share" /></button></header>
        <main>
          <div class="music-disc" :class="{ spinning: musicPlaying }"><img :src="playingMusic.cover_url" :alt="playingMusic.title"><span /></div>
          <div class="music-player-copy"><small>{{ playingMusic.album || $t("musicUi.single") }}</small><h1>{{ playingMusic.title }}</h1><strong>{{ playingMusic.artist }}</strong><p>{{ playingMusic.description }}</p></div>
          <section ref="musicLyrics" class="music-lyrics" :aria-label="$t('musicUi.lyrics')">
            <div v-if="musicLyrics.length" class="music-lyrics-track">
              <button
                v-for="(line, index) in musicLyrics"
                :key="`${line.time}-${index}`"
                ref="musicLyricLines"
                :class="{ active: index === activeLyricIndex, passed: index < activeLyricIndex }"
                @click="seekMusicLyric(line)"
              >{{ line.text }}</button>
            </div>
            <div v-else class="music-lyrics-empty"><i class="el-icon-headset" /><span>{{ $t("musicUi.noLyrics") }}</span></div>
          </section>
          <div class="music-progress">
            <input :aria-label="$t('player.progress')" type="range" min="0" :max="Math.max(1, musicDuration)" step="0.1" :value="musicCurrentTime" :style="{ '--music-progress': `${musicDuration ? musicCurrentTime / musicDuration * 100 : 0}%` }" @input="seekMusic">
            <span>{{ formatPlayerTime(musicCurrentTime) }}</span><span>{{ formatPlayerTime(musicDuration) }}</span>
          </div>
          <div class="music-controls">
            <button :aria-label="$t('musicUi.loopMode')" @click="toggleMusicLoop"><i :class="musicLoop ? 'el-icon-refresh active' : 'el-icon-refresh'" /></button>
            <button :disabled="!playingMusicPrevious" :aria-label="$t('musicUi.previous')" @click="openAdjacentMusic(playingMusicPrevious)"><i class="el-icon-arrow-left" /></button>
            <button class="music-play-toggle" :aria-label="$t(musicPlaying ? 'musicUi.pause' : 'musicUi.play')" data-analytics="music.play_toggle" @click="toggleMusicPlayback"><i :class="musicPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'" /></button>
            <button :disabled="!playingMusicNext" :aria-label="$t('musicUi.next')" @click="openAdjacentMusic(playingMusicNext)"><i class="el-icon-arrow-right" /></button>
            <button :aria-label="$t('musicUi.favorite')" @click="toggleMusicFavorite(playingMusic)"><i :class="playingMusic.collected ? 'el-icon-star-on active' : 'el-icon-star-off'" /></button>
          </div>
        </main>
      </section>
    </transition>

    <transition name="discover-panel">
      <section v-if="rankingVisible" class="discover-overlay ranking-overlay">
        <header class="overlay-header"><button :aria-label="$t('a11y.backDiscover')" @click="rankingVisible = false"><i class="el-icon-back" /></button><strong>{{ rankingTitle }}</strong><button :aria-label="$t('a11y.refreshRanking')" @click="loadRanking"><i class="el-icon-refresh" /></button></header>
        <div class="overlay-scroll ranking-list"><button v-for="(item, index) in rankingList" :key="item.id" @click="openRankingItem(item)"><b>{{ index + 1 }}</b><img :src="item.cover_url" :alt="item.title"><span><strong>{{ item.title }}</strong><p>{{ item.description }}</p><em>{{ item.category_name }} · {{ $t("discoverUi.heat", { value: formatHeat(item.heat) }) }}</em></span><i class="el-icon-arrow-right" /></button></div>
      </section>
    </transition>

    <transition name="discover-fade">
      <game-player
        v-if="gamePlayerVisible && playingGame && gameLaunch"
        :game="playingGame"
        :launch="gameLaunch"
        @close="closeGamePlayer"
        @favorite="toggleGameFavorite"
        @share="shareDiscover('game', playingGame)"
        @finish="finishGame"
      />
    </transition>
  </section>
</template>

<script>
import {
  addDiscoveryEvent,
  addDiscoveryFeedback,
  clearContinueList,
  deleteContinueItem,
  getBookshelfList,
  getContinueList,
  getDiscoverConfig,
  getDiscoverFeatured,
  getDramaCategories,
  getDramaDetail,
  getDramaList,
  getDramaPlay,
  getGameCategories,
  getGameDetail,
  getGameList,
  getGameStart,
  getFavoriteList,
  getMusicCategories,
  getMusicDetail,
  getMusicList,
  getMusicPlay,
  getNovelCategories,
  getNovelChapter,
  getNovelChapters,
  getNovelDetail,
  getNovelList,
  getRanking,
  searchDiscover,
  updateBookshelf,
  updateDramaProgress,
  updateGameFavorite,
  updateGameProgress,
  updateMusicFavorite,
  updateMusicProgress,
  updateNovelAudioProgress,
  updateNovelProgress
} from "../api/discover"
import { getFeed } from "../api/content"
import { sanitizeRichText } from "../utils/richText"
import behaviorAnalytics from "../utils/behaviorAnalytics"
import EmptyState from "./discover/EmptyState.vue"
import DecorationGallery from "./DecorationGallery.vue"
import GamePlayer from "./GamePlayer.vue"
import ModuleHead from "./discover/ModuleHead.vue"
import NovelCard from "./discover/NovelCard.vue"
import ReaderSettings from "./discover/ReaderSettings.vue"

export default {
  name: "DiscoverPage",
  components: { ModuleHead, EmptyState, NovelCard, GamePlayer, ReaderSettings, DecorationGallery },
  props: {
    loggedIn: { type: Boolean, default: false },
    userAvatar: { type: String, default: "" }
  },
  data() {
    return {
      initialized: false, initializingPromise: null, refreshingPromise: null, lastRefreshedAt: 0, loading: false, listLoading: false, pageError: false,
      channels: [{ code: "featured" }, { code: "drama" }, { code: "novel" }, { code: "music" }, { code: "game" }, { code: "decoration" }],
      activeChannel: "featured", scrollPositions: { featured: 0, drama: 0, novel: 0, music: 0, game: 0, decoration: 0 },
      featured: {}, bannerIndex: 0, bannerTimer: null, hotKeywords: [],
      dramaCategories: [], dramaCategory: "recommend", dramaList: [], dramaPage: 1, dramaHasMore: true,
      novelCategories: [], novelCategory: "recommend", novelList: [], novelPage: 1, novelHasMore: true,
      liveTransitionPool: [], liveTransitionLoading: false, liveTransitionVisible: false,
      liveTransitionVideo: null, liveTransitionType: "", liveTransitionTarget: null,
      liveTransitionResumeDrama: false, liveTransitionResumeAudiobook: false,
      musicCategories: [], musicCategory: "recommend", musicList: [], musicPage: 1, musicHasMore: true,
      gameCategories: [], gameCategory: "recommend", gameList: [], gamePage: 1, gameHasMore: true,
      searchVisible: false, searchKeyword: "", searchSubmitted: false, searchLoading: false, searchType: "all",
      searchTypes: [{ code: "all" }, { code: "drama" }, { code: "novel" }, { code: "music" }, { code: "game" }],
      searchResults: { dramas: [], novels: [], music: [], games: [] }, searchHistory: [],
      dramaDetailVisible: false, dramaDetail: null, playerVisible: false, playingDrama: null, playingEpisode: null, playingPrevious: null, playingNext: null, dramaSaveSecond: -1,
      dramaCurrentTime: 0, dramaDuration: 0, dramaPlaying: false, dramaMuted: false, dramaSeeking: false, dramaSeekTime: 0,
      novelDetailVisible: false, novelDetail: null, directoryVisible: false, chapterList: [], chapterOrder: "asc", chapterPage: 1, chapterHasMore: false, chapterLoading: false,
      readerVisible: false, readerData: null, readerPercent: 0, readerSavePercent: -1,
      readerSettingsVisible: false, readerFontSize: 17, readerLineHeight: 2, readerTheme: "paper",
      audiobookVisible: false, audiobookPlaying: false, audiobookTime: 0, audiobookDuration: 0,
      audiobookRate: 1, audiobookSaveSecond: -1, audiobookAutoplayPending: false,
      musicPlayerVisible: false, playingMusic: null, playingMusicPrevious: null, playingMusicNext: null,
      musicCurrentTime: 0, musicDuration: 0, musicPlaying: false, musicLoop: false, musicSaveSecond: -1,
      gamePlayerVisible: false, playingGame: null, gameLaunch: null,
      rankingVisible: false, rankingType: "", rankingTitle: "", rankingList: [],
      libraryVisible: false, libraryTab: "history", libraryLoading: false,
      libraryHistory: [], libraryHistoryPage: 1, libraryHistoryTotal: 0, libraryHistoryHasMore: false,
      libraryShelf: [], libraryShelfPage: 1, libraryShelfTotal: 0, libraryShelfHasMore: false,
      libraryMusic: [], libraryMusicPage: 1, libraryMusicTotal: 0, libraryMusicHasMore: false,
      libraryGame: [], libraryGamePage: 1, libraryGameTotal: 0, libraryGameHasMore: false,
      touchStartX: 0, touchStartY: 0, touchStartedAtTop: false
    }
  },
  computed: {
    currentBanner() {
      return this.featured.banners && this.featured.banners[this.bannerIndex] ? this.featured.banners[this.bannerIndex] : {}
    },
    continueItems() {
      const serverItems = this.featured.continue_list || []
      if (this.loggedIn) return serverItems
      return serverItems.length ? serverItems : this.localContinueItems()
    },
    readerContentHtml() {
      if (!this.readerData || !this.readerData.chapter) return ""
      const chapter = this.readerData.chapter
      const fallback = (chapter.paragraphs || []).map(paragraph => `<p>${this.escapeHtml(paragraph)}</p>`).join("")
      return sanitizeRichText(chapter.content_html || fallback)
    },
    readerStyle() {
      return {
        "--reader-font-size": `${this.readerFontSize}px`,
        "--reader-line-height": this.readerLineHeight
      }
    },
    recommendItems() { return this.featured.recommend_list || [] },
    liveTransitionPrevious() {
      if (this.liveTransitionType === "drama") return this.playingPrevious
      return this.readerData && this.readerData.previous
    },
    playableEpisodeCount() {
      if (!this.dramaDetail || !Array.isArray(this.dramaDetail.episodes)) return 0
      return this.dramaDetail.episodes.length
    },
    activeChannelIndex() {
      return this.channels.findIndex(channel => channel.code === this.activeChannel)
    },
    canSwitchPreviousChannel() {
      return this.activeChannelIndex > 0
    },
    canSwitchNextChannel() {
      return this.activeChannelIndex >= 0 && this.activeChannelIndex < this.channels.length - 1
    },
    hasCurrentData() {
      if (this.activeChannel === "featured") return !!(this.featured.hot_dramas && this.featured.hot_dramas.length)
      if (this.activeChannel === "drama") return this.dramaList.length > 0
      if (this.activeChannel === "novel") return this.novelList.length > 0
      if (this.activeChannel === "music") return this.musicList.length > 0
      if (this.activeChannel === "game") return this.gameList.length > 0
      return true
    },
    dramaCategoryName() {
      const item = this.dramaCategories.find(category => category.code === this.dramaCategory)
      return item ? this.categoryLabel("drama", item) : this.$t("discoverUi.drama")
    },
    novelCategoryName() {
      const item = this.novelCategories.find(category => category.code === this.novelCategory)
      return item ? this.categoryLabel("novel", item) : this.$t("discoverUi.novel")
    },
    musicCategoryName() {
      const item = this.musicCategories.find(category => category.code === this.musicCategory)
      return item ? this.categoryLabel("music", item) : this.$t("discoverUi.music")
    },
    gameCategoryName() {
      const item = this.gameCategories.find(category => category.code === this.gameCategory)
      return item ? this.categoryLabel("game", item) : this.$t("discoverUi.game")
    },
    activeLibraryItems() {
      return this.libraryTab === "history"
        ? this.libraryHistory
        : this.libraryTab === "bookshelf"
          ? this.libraryShelf
          : this.libraryTab === "music"
            ? this.libraryMusic
            : this.libraryGame
    },
    activeLibraryHasMore() {
      return this.libraryTab === "history"
        ? this.libraryHistoryHasMore
        : this.libraryTab === "bookshelf"
          ? this.libraryShelfHasMore
          : this.libraryTab === "music"
            ? this.libraryMusicHasMore
            : this.libraryGameHasMore
    },
    libraryEmptyKey() {
      if (this.libraryTab === "history") return "discoverLibrary.emptyHistory"
      if (this.libraryTab === "bookshelf") return "discoverLibrary.emptyShelf"
      return this.libraryTab === "music" ? "discoverLibrary.emptyMusicFavorites" : "discoverLibrary.emptyGameFavorites"
    },
    musicLyrics() {
      if (!this.playingMusic || !Array.isArray(this.playingMusic.lyrics)) return []
      return this.playingMusic.lyrics
        .map(line => ({ time: Math.max(0, Number(line.time) || 0), text: String(line.text || "").trim() }))
        .filter(line => line.text)
        .sort((left, right) => left.time - right.time)
    },
    activeLyricIndex() {
      let active = -1
      for (let index = 0; index < this.musicLyrics.length; index += 1) {
        if (this.musicLyrics[index].time > this.musicCurrentTime + 0.15) break
        active = index
      }
      return active
    }
  },
  watch: {
    loggedIn() {
      if (this.initialized) this.refreshCurrent()
      if (this.libraryVisible) this.loadLibrary(true)
    },
    activeLyricIndex() {
      this.$nextTick(this.scrollActiveLyric)
    }
  },
  methods: {
    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;")
    },
    channelLabel(channel) {
      const key = `discoverUi.${channel.code}`
      const translated = this.$t(key)
      return translated === key ? (channel.title || channel.name || channel.code) : translated
    },
    categoryLabel(type, category) {
      if (category.language && category.language !== "zh-CN") return category.name
      const key = `discoverCategory.${type}.${category.code}`
      const translated = this.$t(key)
      return translated === key ? category.name : translated
    },
    quickEntryLabel(item) {
      return { drama: this.$t("discoverUi.drama"), novel: this.$t("discoverUi.novel"), music: this.$t("discoverUi.music"), game: this.$t("discoverUi.game"), drama_rank: this.$t("discoverUi.dramaHotRank"), novel_rank: this.$t("discoverUi.novelHotRank"), music_rank: this.$t("discoverUi.musicHotRank") }[item.code] || item.name
    },
    rankingLabel(code) {
      return { drama_hot: this.$t("discoverUi.dramaHotRank"), novel_hot: this.$t("discoverUi.novelHotRank"), novel_new: this.$t("discoverUi.novelNewRank"), novel_finished: this.$t("discoverUi.novelFinishedRank"), music_hot: this.$t("discoverUi.musicHotRank"), music_new: this.$t("discoverUi.musicNewRank"), game_hot: this.$t("discoverUi.gameHotRank"), game_new: this.$t("discoverUi.gameNewRank") }[code] || code
    },
    continueProgress(item) {
      if (item.content_type === "game") return this.$t("gameUi.bestScore", { score: this.$n(item.best_score || 0) })
      if (item.content_type === "music") return this.$t("discoverUi.progressMusic", { time: this.formatPlayerTime(item.position_second) })
      return this.$t(item.content_type === "drama" ? "discoverUi.progressEpisode" : "discoverUi.progressChapter", { count: item.content_type === "drama" ? item.episode_no : item.chapter_no })
    },
    continueActionLabel(item) {
      return this.$t(item.content_type === "game" ? "discoverUi.continuePlay" : item.content_type === "music" ? "discoverUi.continueListen" : item.content_type === "drama" ? "discoverUi.continueWatch" : "discoverUi.continueRead")
    },
    continueTypeLabel(item) {
      return this.$t(item.content_type === "game" ? "discoverUi.game" : item.content_type === "music" ? "discoverUi.music" : item.content_type === "drama" ? "discoverUi.drama" : "discoverUi.novel")
    },
    mixedTypeLabel(item) {
      return this.$t(item.content_type === "game" ? "discoverUi.game" : item.content_type === "music" ? "discoverUi.music" : item.content_type === "drama" ? "discoverUi.drama" : "discoverUi.novel")
    },
    bannerTypeLabel(type) {
      return this.$t(type === "game" ? "gameUi.bannerType" : type === "music" ? "musicUi.bannerType" : type === "drama" ? "musicUi.dramaBannerType" : "musicUi.novelBannerType")
    },
    badgeLabel(value) {
      const key = { "热": "hot", "热播": "hot", "热门": "hot", "新": "new", "新剧": "new", "新歌": "newMusic", "编辑推荐": "editorsPick", "完结": "finished", "独播": "exclusive", "限免": "limitedFree", "今日更新": "updatedToday", "热读": "popularRead", "新书": "newBook", "独家": "exclusive", "爆款": "hit" }[value]
      return key ? this.$t(`discoverBadge.${key}`) : value
    },
    // 首次进入只加载精选；短剧和小说频道在用户打开时再请求，减少首屏等待。
    initialize() {
      if (this.initializingPromise) return this.initializingPromise
      if (this.initialized) return Promise.resolve()
      this.initialized = true
      this.searchHistory = this.readLocal("starlive_discover_searches", [])
      this.loading = true
      this.pageError = false
      this.initializingPromise = Promise.all([getDiscoverConfig(), getDiscoverFeatured()]).then(([configRes, featuredRes]) => {
        if (Array.isArray(configRes.data.channels)) this.channels = configRes.data.channels
        if (!this.channels.some(item => item.code === "decoration")) this.channels.push({ code: "decoration" })
        this.hotKeywords = Array.isArray(configRes.data.hot_keywords) && configRes.data.hot_keywords.length
          ? configRes.data.hot_keywords
          : ["one", "two", "three", "four", "five", "six"].map(key => this.$t(`discoverSearchTerms.${key}`))
        this.featured = this.decorateFeatured(featuredRes.data)
        this.startBannerTimer()
        this.track("enter_discover", "channel", 0, "featured")
        behaviorAnalytics.enter("discover", this.activeChannel)
        this.lastRefreshedAt = Date.now()
      }).catch(() => {
        this.pageError = true
      }).finally(() => {
        this.loading = false
        this.$nextTick(this.ensureActiveChannelVisible)
        this.initializingPromise = null
      })
      return this.initializingPromise
    },
    activate() {
      if (!this.initialized || this.initializingPromise) return this.initialize()
      // 已访问页面立即恢复；一分钟后才后台静默同步，避免每次一级 Tab 切换都等待网络和重渲染。
      if (Date.now() - this.lastRefreshedAt >= 60000) return this.refreshCurrent(true)
      return Promise.resolve()
    },
    // 切换语言后必须重新请求运营配置、分类和内容，不能继续复用上一语言的缓存。
    async reloadLocale() {
      const active = this.activeChannel
      this.initialized = false
      this.lastRefreshedAt = 0
      this.dramaCategories = []
      this.novelCategories = []
      this.musicCategories = []
      this.gameCategories = []
      this.dramaList = []
      this.novelList = []
      this.musicList = []
      this.gameList = []
      this.liveTransitionPool = []
      await this.initialize()
      if (active === "drama") await this.loadDramaInitial()
      if (active === "novel") await this.loadNovelInitial()
      if (active === "music") await this.loadMusicInitial()
      if (active === "game") await this.loadGameInitial()
      if (this.playingMusic) {
        try {
          const detailRes = await getMusicDetail({ id: this.playingMusic.id })
          const localizedTrack = this.decorateMusic(detailRes.data.track)
          Object.assign(this.playingMusic, localizedTrack)
          this.playingMusicPrevious = detailRes.data.previous
          this.playingMusicNext = detailRes.data.next
          this.emitMusicState()
          this.$nextTick(this.scrollActiveLyric)
        } catch (error) { /* 保留当前播放状态，接口层已处理提示。 */ }
      }
      this.$nextTick(this.ensureActiveChannelVisible)
    },
    decorateFeatured(data) {
      return {
        ...data,
        hot_dramas: (data.hot_dramas || []).map(this.decorateDrama),
        hot_novels: (data.hot_novels || []).map(this.decorateNovel),
        hot_music: (data.hot_music || []).map(this.decorateMusic),
        hot_games: (data.hot_games || []).map(this.decorateGame),
        recommend_list: (data.recommend_list || []).map(item => item.content_type === "game" ? this.decorateGame(item) : item.content_type === "music" ? this.decorateMusic(item) : item.content_type === "novel" ? this.decorateNovel(item) : this.decorateDrama(item))
      }
    },
    decorateDrama(drama) {
      const local = this.readLocal("starlive_drama_history", {})[drama.id]
      return { ...drama, progress: drama.progress || (local ? { ...local, percent: local.duration ? Math.min(100, Math.round(local.position_second / local.duration * 100)) : 0 } : null) }
    },
    decorateNovel(novel) {
      const localProgress = this.readLocal("starlive_novel_history", {})[novel.id]
      const localShelf = this.readLocal("starlive_novel_shelf", [])
      return { ...novel, progress: novel.progress || localProgress || null, collected: this.loggedIn ? !!novel.collected : localShelf.includes(Number(novel.id)) }
    },
    decorateMusic(track) {
      const localProgress = this.readLocal("starlive_music_history", {})[track.id]
      const localFavorites = this.readLocal("starlive_music_favorites", []).map(Number)
      return { ...track, progress: track.progress || localProgress || null, collected: this.loggedIn ? !!track.collected : localFavorites.includes(Number(track.id)) }
    },
    decorateGame(game) {
      const localProgress = this.readLocal("starlive_game_history", {})[game.id]
      const localFavorites = this.readLocal("starlive_game_favorites", []).map(Number)
      return { ...game, progress: game.progress || localProgress || null, collected: this.loggedIn ? !!game.collected : localFavorites.includes(Number(game.id)) }
    },
    async switchChannel(code) {
      // 三个频道分别记录滚动位置，从详情或其他频道返回时恢复浏览现场。
      const currentScroll = this.$refs.scroll
      if (this.activeChannel === code) {
        if (currentScroll) currentScroll.scrollTo({ top: 0, behavior: "smooth" })
        return
      }
      if (currentScroll) this.scrollPositions[this.activeChannel] = currentScroll.scrollTop
      this.activeChannel = code
      behaviorAnalytics.enter("discover", code)
      this.pageError = false
      this.track("switch_channel", "channel", 0, code)
      if (code === "drama" && !this.dramaCategories.length) await this.loadDramaInitial()
      if (code === "novel" && !this.novelCategories.length) await this.loadNovelInitial()
      if (code === "music" && !this.musicCategories.length) await this.loadMusicInitial()
      if (code === "game" && !this.gameCategories.length) await this.loadGameInitial()
      this.$nextTick(() => {
        if (this.$refs.scroll) this.$refs.scroll.scrollTop = this.scrollPositions[code] || 0
        this.ensureActiveChannelVisible()
      })
    },
    stepChannel(offset) {
      const targetIndex = this.activeChannelIndex + Number(offset || 0)
      const target = this.channels[targetIndex]
      if (target) this.switchChannel(target.code)
    },
    ensureActiveChannelVisible() {
      const tabs = this.$refs.channelTabs
      if (!tabs) return
      const active = Array.from(tabs.children).find(element => element.dataset.channel === this.activeChannel)
      if (active && typeof active.scrollIntoView === "function") {
        active.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
      }
    },
    async loadDramaInitial() {
      this.loading = true
      try {
        const categoryRes = await getDramaCategories()
        this.dramaCategories = categoryRes.data.list
        const defaultCategory = this.dramaCategories.find(item => Number(item.is_default) === 1)
        this.dramaCategory = defaultCategory ? defaultCategory.code : "recommend"
        await this.loadDramaList(true)
      } catch (error) {
        this.pageError = true
      } finally {
        this.loading = false
      }
    },
    async loadNovelInitial() {
      this.loading = true
      try {
        const categoryRes = await getNovelCategories()
        this.novelCategories = categoryRes.data.list
        const defaultCategory = this.novelCategories.find(item => Number(item.is_default) === 1)
        this.novelCategory = defaultCategory ? defaultCategory.code : "recommend"
        await this.loadNovelList(true)
      } catch (error) {
        this.pageError = true
      } finally {
        this.loading = false
      }
    },
    async loadMusicInitial() {
      this.loading = true
      try {
        const categoryRes = await getMusicCategories()
        this.musicCategories = categoryRes.data.list
        const defaultCategory = this.musicCategories.find(item => Number(item.is_default) === 1)
        this.musicCategory = defaultCategory ? defaultCategory.code : "recommend"
        await this.loadMusicList(true)
      } catch (error) {
        this.pageError = true
      } finally {
        this.loading = false
      }
    },
    async loadGameInitial() {
      this.loading = true
      try {
        const categoryRes = await getGameCategories()
        this.gameCategories = categoryRes.data.list
        const defaultCategory = this.gameCategories.find(item => Number(item.is_default) === 1)
        this.gameCategory = defaultCategory ? defaultCategory.code : "recommend"
        await this.loadGameList(true)
      } catch (error) {
        this.pageError = true
      } finally {
        this.loading = false
      }
    },
    async loadDramaList(reset = false) {
      if (this.listLoading || (!reset && !this.dramaHasMore)) return
      this.listLoading = true
      if (reset) { this.dramaPage = 1; this.dramaHasMore = true }
      try {
        const res = await getDramaList({ category: this.dramaCategory, page: this.dramaPage, limit: 8 })
        const next = res.data.list.map(this.decorateDrama)
        this.dramaList = reset ? next : this.uniqueById(this.dramaList.concat(next))
        this.dramaHasMore = res.data.has_more
        if (res.data.has_more) this.dramaPage += 1
      } finally {
        this.listLoading = false
      }
    },
    async loadNovelList(reset = false) {
      if (this.listLoading || (!reset && !this.novelHasMore)) return
      this.listLoading = true
      if (reset) { this.novelPage = 1; this.novelHasMore = true }
      try {
        const res = await getNovelList({ category: this.novelCategory, page: this.novelPage, limit: 8 })
        const next = res.data.list.map(this.decorateNovel)
        this.novelList = reset ? next : this.uniqueById(this.novelList.concat(next))
        this.novelHasMore = res.data.has_more
        if (res.data.has_more) this.novelPage += 1
      } finally {
        this.listLoading = false
      }
    },
    async loadMusicList(reset = false) {
      if (this.listLoading || (!reset && !this.musicHasMore)) return
      this.listLoading = true
      if (reset) { this.musicPage = 1; this.musicHasMore = true }
      try {
        const res = await getMusicList({ category: this.musicCategory, page: this.musicPage, limit: 12 })
        const next = res.data.list.map(this.decorateMusic)
        this.musicList = reset ? next : this.uniqueById(this.musicList.concat(next))
        this.musicHasMore = res.data.has_more
        if (res.data.has_more) this.musicPage += 1
      } finally {
        this.listLoading = false
      }
    },
    async loadGameList(reset = false) {
      if (this.listLoading || (!reset && !this.gameHasMore)) return
      this.listLoading = true
      if (reset) { this.gamePage = 1; this.gameHasMore = true }
      try {
        const res = await getGameList({ category: this.gameCategory, page: this.gamePage, limit: 12 })
        const next = res.data.list.map(this.decorateGame)
        this.gameList = reset ? next : this.uniqueById(this.gameList.concat(next))
        this.gameHasMore = res.data.has_more
        if (res.data.has_more) this.gamePage += 1
      } finally {
        this.listLoading = false
      }
    },
    changeDramaCategory(code) {
      if (this.dramaCategory === code) return
      this.dramaCategory = code
      this.scrollPositions.drama = 0
      if (this.$refs.scroll) this.$refs.scroll.scrollTop = 0
      this.loadDramaList(true)
    },
    changeNovelCategory(code) {
      if (this.novelCategory === code) return
      this.novelCategory = code
      this.scrollPositions.novel = 0
      if (this.$refs.scroll) this.$refs.scroll.scrollTop = 0
      this.loadNovelList(true)
    },
    changeMusicCategory(code) {
      if (this.musicCategory === code) return
      this.musicCategory = code
      this.scrollPositions.music = 0
      if (this.$refs.scroll) this.$refs.scroll.scrollTop = 0
      this.loadMusicList(true)
    },
    changeGameCategory(code) {
      if (this.gameCategory === code) return
      this.gameCategory = code
      this.scrollPositions.game = 0
      if (this.$refs.scroll) this.$refs.scroll.scrollTop = 0
      this.loadGameList(true)
    },
    async refreshCurrent(silent = false) {
      if (this.refreshingPromise) return this.refreshingPromise
      this.refreshingPromise = this.performCurrentRefresh(silent)
      try {
        await this.refreshingPromise
      } finally {
        this.refreshingPromise = null
      }
    },
    async performCurrentRefresh(silent = false) {
      this.loading = true
      this.pageError = false
      try {
        if (this.activeChannel === "featured") {
          const res = await getDiscoverFeatured()
          this.featured = this.decorateFeatured(res.data)
        } else if (this.activeChannel === "drama") {
          await this.loadDramaList(true)
        } else if (this.activeChannel === "novel") {
          await this.loadNovelList(true)
        } else if (this.activeChannel === "music") {
          await this.loadMusicList(true)
        } else if (this.activeChannel === "game") {
          await this.loadGameList(true)
        } else if (this.$refs.decorationShop) {
          await this.$refs.decorationShop.load()
        }
        this.lastRefreshedAt = Date.now()
        // 手动与后台刷新都通过当前频道加载状态反馈，成功后不再弹提示框。
        this.track("pull_refresh", "channel", 0, this.activeChannel)
      } catch (error) {
        this.pageError = true
      } finally {
        this.loading = false
      }
    },
    handleScroll(event) {
      const element = event.target
      this.scrollPositions[this.activeChannel] = element.scrollTop
      if (element.scrollHeight - element.scrollTop - element.clientHeight < 220) {
        if (this.activeChannel === "drama") this.loadDramaList()
        if (this.activeChannel === "novel") this.loadNovelList()
        if (this.activeChannel === "music") this.loadMusicList()
        if (this.activeChannel === "game") this.loadGameList()
      }
    },
    handleTouchStart(event) {
      const touch = event.changedTouches[0]
      this.touchStartX = touch.clientX
      this.touchStartY = touch.clientY
      this.touchStartedAtTop = !this.$refs.scroll || this.$refs.scroll.scrollTop <= 0
    },
    handleTouchEnd(event) {
      if (this.searchVisible || this.libraryVisible || this.dramaDetailVisible || this.novelDetailVisible || this.playerVisible || this.musicPlayerVisible || this.gamePlayerVisible || this.readerVisible || this.directoryVisible || this.rankingVisible) return
      const touch = event.changedTouches[0]
      const deltaX = touch.clientX - this.touchStartX
      const deltaY = touch.clientY - this.touchStartY
      if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3) {
        const index = this.channels.findIndex(item => item.code === this.activeChannel)
        const target = deltaX < 0 ? index + 1 : index - 1
        if (this.channels[target]) this.switchChannel(this.channels[target].code)
      } else if (this.touchStartedAtTop && deltaY > 90 && Math.abs(deltaY) > Math.abs(deltaX)) {
        this.refreshCurrent()
      }
    },
    openShortcut(item) {
      if (item.code === "drama") this.switchChannel("drama")
      else if (item.code === "novel") this.switchChannel("novel")
      else if (item.code === "music") this.switchChannel("music")
      else if (item.code === "game") this.switchChannel("game")
      else if (item.code === "drama_rank") this.openRanking("drama_hot")
      else if (item.code === "music_rank") this.openRanking("music_hot")
      else this.openRanking("novel_hot")
      this.track("click_shortcut", "channel", 0, item.code)
    },
    openBanner(banner) {
      if (!banner) return
      this.track("click_banner", "banner", banner.id, "featured")
      if (banner.content_type === "drama") this.openDrama(banner.target_id)
      else if (banner.content_type === "music") this.openMusic({ id: banner.target_id })
      else if (banner.content_type === "game") this.openGame({ id: banner.target_id })
      else if (banner.content_type === "novel") this.openNovel(banner.target_id)
      else this.openBannerUrl(banner)
    },
    openBannerUrl(banner) {
      const target = String(banner.target_url || "").trim()
      const isInternal = banner.content_type === "h5" && target.startsWith("/") && !target.startsWith("//")
      const isHttps = /^https:\/\//i.test(target)
      if (!isInternal && !isHttps) return
      if (banner.content_type === "external") window.open(target, "_blank", "noopener,noreferrer")
      else window.location.assign(target)
    },
    shareDiscover(contentType, item) {
      if (!item || !item.id) return
      this.$emit("share-content", { ...item, content_type: contentType })
    },
    startBannerTimer() {
      window.clearInterval(this.bannerTimer)
      this.bannerTimer = window.setInterval(() => {
        const count = this.featured.banners ? this.featured.banners.length : 0
        if (count > 1) this.bannerIndex = (this.bannerIndex + 1) % count
      }, 5000)
    },
    async openDrama(id, autoplay = false) {
      try {
        const res = await getDramaDetail({ id })
        this.dramaDetail = { ...res.data, drama: this.decorateDrama(res.data.drama) }
        this.dramaDetailVisible = true
        this.searchVisible = false
        this.rankingVisible = false
        this.track("click_drama", "drama", id, this.activeChannel)
        if (autoplay) this.$nextTick(() => this.playDrama())
      } catch (error) { /* 请求封装统一提示。 */ }
    },
    closeDramaDetail() { this.dramaDetailVisible = false },
    async playDrama(episode) {
      const drama = this.dramaDetail.drama
      let selected = episode
      if (!selected && drama.progress) selected = this.dramaDetail.episodes.find(item => Number(item.id) === Number(drama.progress.episode_id))
      if (!selected) selected = this.dramaDetail.episodes[0]
      if (!selected) return
      try {
        const res = await getDramaPlay({ drama_id: drama.id, episode_id: selected.id })
        if (!res.data.can_play) { this.$notice.warning(this.$t(`errors.${res.data.reason_code}`)); return }
        this.playingDrama = drama
        this.playingEpisode = res.data.episode
        this.playingPrevious = res.data.previous
        this.playingNext = res.data.next
        this.playingEpisode.restore_position = res.data.position_second || 0
        this.playerVisible = true
        this.dramaSaveSecond = -1
        this.dramaCurrentTime = 0
        this.dramaDuration = Number(this.playingEpisode.duration) || 0
        this.dramaPlaying = false
        this.dramaSeeking = false
        this.dramaSeekTime = 0
        this.track("drama_start_play", "drama", drama.id, "player")
      } catch (error) { /* 请求封装统一提示。 */ }
    },
    restoreDramaPosition() {
      const player = this.$refs.dramaPlayer
      if (!player) return
      this.dramaDuration = Number(player.duration) || Number(this.playingEpisode.duration) || 0
      player.muted = this.dramaMuted
      if (this.playingEpisode.restore_position > 0 && this.playingEpisode.restore_position < player.duration - 2) player.currentTime = this.playingEpisode.restore_position
      this.dramaCurrentTime = Number(player.currentTime) || 0
    },
    handleDramaTimeUpdate(event) {
      this.dramaDuration = Number(event.target.duration) || Number(this.playingEpisode.duration) || 0
      // 拖动期间保留手指指向的位置，避免 timeupdate 把滑块拉回旧进度。
      if (this.dramaSeeking) return

      this.dramaCurrentTime = Number(event.target.currentTime) || 0
      const second = Math.floor(event.target.currentTime)
      if (second > 0 && second % 5 === 0 && second !== this.dramaSaveSecond) {
        this.dramaSaveSecond = second
        this.saveDramaProgress(second, Math.floor(event.target.duration || this.playingEpisode.duration))
      }
    },
    saveDramaProgress(position, duration) {
      const data = { drama_id: this.playingDrama.id, episode_id: this.playingEpisode.id, episode_no: this.playingEpisode.episode_no, position_second: position, duration }
      if (this.loggedIn) {
        updateDramaProgress(data).catch(() => {})
      } else {
        // 游客进度只保存在当前设备，登录账号由服务端跨设备同步。
        const history = this.readLocal("starlive_drama_history", {})
        history[this.playingDrama.id] = { ...data, title: this.playingDrama.title, cover_url: this.playingDrama.cover_url, update_time: Date.now() }
        this.writeLocal("starlive_drama_history", history)
      }
      this.playingDrama.progress = { ...data, percent: duration ? Math.min(100, Math.round(position / duration * 100)) : 0 }
    },
    closePlayer() {
      const player = this.$refs.dramaPlayer
      if (player && this.playingDrama && this.playingEpisode) this.saveDramaProgress(Math.floor(player.currentTime), Math.floor(player.duration || this.playingEpisode.duration))
      this.playerVisible = false
      this.playingEpisode = null
      this.dramaPlaying = false
      this.dramaSeeking = false
    },
    toggleDramaPlayback() {
      const player = this.$refs.dramaPlayer
      if (!player) return
      if (player.paused) player.play().catch(() => {})
      else player.pause()
    },
    toggleDramaMute() {
      const player = this.$refs.dramaPlayer
      this.dramaMuted = !this.dramaMuted
      if (player) player.muted = this.dramaMuted
    },
    beginDramaSeek() {
      this.dramaSeeking = true
      this.dramaSeekTime = this.dramaCurrentTime
    },
    seekDrama(event) {
      const player = this.$refs.dramaPlayer
      if (!player) return
      this.dramaSeeking = true
      const next = Math.min(this.dramaDuration || 0, Math.max(0, Number(event.target.value) || 0))
      player.currentTime = next
      this.dramaSeekTime = next
      this.dramaCurrentTime = next
    },
    finishDramaSeek(event) {
      if (!this.dramaSeeking) return

      const player = this.$refs.dramaPlayer
      if (!player) {
        this.dramaSeeking = false
        return
      }

      const next = Math.min(this.dramaDuration || 0, Math.max(0, Number(event && event.target && event.target.value) || this.dramaSeekTime))
      player.currentTime = next
      this.dramaCurrentTime = next
      this.dramaSeekTime = next
      this.dramaSeeking = false
      this.saveDramaProgress(Math.floor(next), Math.floor(player.duration || this.playingEpisode.duration))
    },
    formatPlayerTime(value) {
      const total = Math.max(0, Math.floor(Number(value) || 0))
      const minutes = Math.floor(total / 60)
      return `${minutes}:${String(total % 60).padStart(2, "0")}`
    },
    playAdjacentEpisode(target) {
      if (!target) return
      const episode = this.dramaDetail.episodes.find(item => Number(item.id) === Number(target.id))
      this.playDrama(episode)
    },
    requestNextEpisode() {
      if (this.playingNext) this.requestForwardTransition("drama", this.playingNext)
    },
    playNextEpisode() {
      if (this.playingNext) this.playAdjacentEpisode(this.playingNext)
    },
    requestNextChapter() {
      if (this.readerData && this.readerData.next) this.requestForwardTransition("novel", this.readerData.next)
    },
    async requestForwardTransition(type, target) {
      if (!target || this.liveTransitionLoading) return
      const countKey = `starlive_live_transition_count_${type}`
      const lastKey = `starlive_live_transition_last_${type}`
      const count = Number(window.sessionStorage.getItem(countKey) || 0) + 1
      const lastShown = Number(window.sessionStorage.getItem(lastKey) || 0)
      window.sessionStorage.setItem(countKey, String(count))

      // 章节间直播是低频推荐：每三次向后切换最多出现一次，并设置五分钟冷却。
      if (count % 3 !== 0 || Date.now() - lastShown < 5 * 60 * 1000) {
        this.performForwardTransition(type, target)
        return
      }

      this.liveTransitionLoading = true
      try {
        const live = await this.pickLiveTransition()
        if (!live) {
          this.performForwardTransition(type, target)
          return
        }
        window.sessionStorage.setItem(lastKey, String(Date.now()))
        this.openLiveTransition(type, target, live)
      } catch (error) {
        // 直播推荐失败不能阻断短剧或小说的下一项操作。
        this.performForwardTransition(type, target)
      } finally {
        this.liveTransitionLoading = false
      }
    },
    performForwardTransition(type, target) {
      if (type === "drama") this.playAdjacentEpisode(target)
      else this.openReader(target && target.id)
    },
    async pickLiveTransition() {
      const seenKey = "starlive_live_transition_seen"
      const seen = new Set(this.readLocal(seenKey, []).map(Number))
      let candidate = this.liveTransitionPool.find(item => !seen.has(Number(item.id)))
      if (!candidate) {
        const response = await getFeed({ channel: "live", page: 1, limit: 12 })
        this.liveTransitionPool = (response.data.list || []).filter(item => item.content_type === "live" && Number(item.live_status) === 1 && item.available)
        candidate = this.liveTransitionPool.find(item => !seen.has(Number(item.id)))
      }
      // 当前全部直播都展示过后开启新一轮，避免少量直播场景永久没有推荐卡片。
      if (!candidate && this.liveTransitionPool.length) {
        seen.clear()
        candidate = this.liveTransitionPool[0]
      }
      if (!candidate) return null
      seen.add(Number(candidate.id))
      this.writeLocal(seenKey, Array.from(seen).slice(-50))
      return candidate
    },
    openLiveTransition(type, target, live) {
      const dramaPlayer = this.$refs.dramaPlayer
      const audiobook = this.$refs.novelAudioPlayer
      this.liveTransitionResumeDrama = Boolean(type === "drama" && dramaPlayer && !dramaPlayer.paused)
      this.liveTransitionResumeAudiobook = Boolean(type === "novel" && audiobook && !audiobook.paused)
      if (dramaPlayer) dramaPlayer.pause()
      if (audiobook) audiobook.pause()
      this.liveTransitionType = type
      this.liveTransitionTarget = target
      this.liveTransitionVideo = live
      this.liveTransitionVisible = true
      this.track("live_transition_show", "live", live.id, type)
    },
    resetLiveTransition() {
      this.liveTransitionVisible = false
      this.liveTransitionVideo = null
      this.liveTransitionType = ""
      this.liveTransitionTarget = null
      this.liveTransitionResumeDrama = false
      this.liveTransitionResumeAudiobook = false
    },
    closeLiveTransition() {
      const resumeDrama = this.liveTransitionResumeDrama
      const resumeAudiobook = this.liveTransitionResumeAudiobook
      this.resetLiveTransition()
      this.$nextTick(() => {
        if (resumeDrama && this.$refs.dramaPlayer) this.$refs.dramaPlayer.play().catch(() => {})
        if (resumeAudiobook && this.$refs.novelAudioPlayer) this.$refs.novelAudioPlayer.play().catch(() => {})
      })
    },
    continueLiveTransition() {
      const type = this.liveTransitionType
      const target = this.liveTransitionTarget
      this.resetLiveTransition()
      this.$nextTick(() => this.performForwardTransition(type, target))
    },
    openLiveTransitionRoom() {
      const live = this.liveTransitionVideo
      if (!live || !live.available) return
      this.resetLiveTransition()
      this.$emit("open-live", live)
    },
    goPreviousFromLive() {
      const type = this.liveTransitionType
      const previous = this.liveTransitionPrevious
      if (!previous) return
      this.resetLiveTransition()
      this.$nextTick(() => {
        if (type === "drama") this.playAdjacentEpisode(previous)
        else this.openReader(previous.id)
      })
    },
    async openMusic(track, options = {}) {
      const id = Number(track && track.id ? track.id : track)
      if (!id) return
      const audiobook = this.$refs.novelAudioPlayer
      if (audiobook && !audiobook.paused) {
        this.saveAudiobookProgress()
        audiobook.pause()
      }
      try {
        const [detailRes, playRes] = await Promise.all([getMusicDetail({ id }), getMusicPlay({ id })])
        this.playingMusic = this.decorateMusic(playRes.data.track)
        this.playingMusic.restore_position = Number(playRes.data.position_second || (this.playingMusic.progress && this.playingMusic.progress.position_second) || 0)
        this.playingMusicPrevious = detailRes.data.previous
        this.playingMusicNext = detailRes.data.next
        this.musicCurrentTime = 0
        this.musicDuration = Number(this.playingMusic.duration) || 0
        this.musicSaveSecond = -1
        this.musicPlaying = false
        this.musicPlayerVisible = options.showFull !== false
        this.searchVisible = false
        this.rankingVisible = false
        await this.$nextTick()
        this.emitMusicState()
        this.track("music_start_play", "music", id, this.activeChannel)
      } catch (error) { /* 请求封装统一提示。 */ }
    },
    async playMusicFromCard(id) {
      const musicId = Number(id)
      if (!musicId) return
      if (!this.playingMusic || Number(this.playingMusic.id) !== musicId) {
        const previousPlayer = this.$refs.musicPlayer
        if (previousPlayer && this.playingMusic) {
          this.saveMusicProgress(Math.floor(previousPlayer.currentTime), Math.floor(previousPlayer.duration || this.playingMusic.duration))
          previousPlayer.pause()
        }
        await this.openMusic({ id: musicId }, { showFull: false })
      } else {
        this.musicPlayerVisible = false
      }
      if (!this.playingMusic || Number(this.playingMusic.id) !== musicId) return
      const player = this.$refs.musicPlayer
      if (player && player.paused) {
        try { await player.play() } catch (error) { this.handleMusicError() }
      }
      this.emitMusicState()
    },
    restoreMusicPosition() {
      const player = this.$refs.musicPlayer
      if (!player || !this.playingMusic) return
      this.musicDuration = Number(player.duration) || Number(this.playingMusic.duration) || 0
      const position = Number(this.playingMusic.restore_position) || 0
      if (position > 0 && position < this.musicDuration - 2) player.currentTime = position
      this.musicCurrentTime = Number(player.currentTime) || 0
      this.emitMusicState()
    },
    handleMusicTimeUpdate(event) {
      this.musicCurrentTime = Number(event.target.currentTime) || 0
      this.musicDuration = Number(event.target.duration) || Number(this.playingMusic.duration) || 0
      const second = Math.floor(this.musicCurrentTime)
      if (second > 0 && second % 5 === 0 && second !== this.musicSaveSecond) {
        this.musicSaveSecond = second
        this.saveMusicProgress(second, Math.floor(this.musicDuration))
      }
      this.emitMusicState()
    },
    handleMusicPlay() {
      this.musicPlaying = true
      this.emitMusicState()
    },
    handleMusicPause() {
      this.musicPlaying = false
      this.emitMusicState()
    },
    saveMusicProgress(position, duration) {
      if (!this.playingMusic) return
      const data = { music_id: this.playingMusic.id, position_second: position, duration }
      if (this.loggedIn) {
        updateMusicProgress(data).catch(() => {})
      } else {
        const history = this.readLocal("starlive_music_history", {})
        history[this.playingMusic.id] = { ...data, title: this.playingMusic.title, artist: this.playingMusic.artist, cover_url: this.playingMusic.cover_url, update_time: Date.now() }
        this.writeLocal("starlive_music_history", history)
      }
      const progress = { ...data, percent: duration ? Math.min(100, Math.round(position / duration * 100)) : 0 }
      this.playingMusic.progress = progress
      this.syncMusicState(this.playingMusic.id, { progress })
    },
    collapseMusicPlayer() {
      const player = this.$refs.musicPlayer
      if (player && this.playingMusic) {
        this.saveMusicProgress(Math.floor(player.currentTime), Math.floor(player.duration || this.playingMusic.duration))
      }
      this.musicPlayerVisible = false
      this.emitMusicState()
    },
    closeMusicPlayer() {
      const player = this.$refs.musicPlayer
      const musicId = this.playingMusic ? Number(this.playingMusic.id) : 0
      if (player && this.playingMusic) {
        // 真正关闭前保存最后进度，避免用户下次打开时回到旧位置。
        this.saveMusicProgress(Math.floor(player.currentTime), Math.floor(player.duration || this.playingMusic.duration))
        player.pause()
      }
      this.musicPlaying = false
      this.musicPlayerVisible = false
      this.playingMusic = null
      this.playingMusicPrevious = null
      this.playingMusicNext = null
      this.musicCurrentTime = 0
      this.musicDuration = 0
      this.musicSaveSecond = -1
      this.emitMusicState()
      if (musicId) this.track("music_close", "music", musicId, "mini_player")
    },
    expandMusicPlayer() {
      if (!this.playingMusic) return
      this.musicPlayerVisible = true
      this.emitMusicState()
      this.$nextTick(this.scrollActiveLyric)
    },
    toggleMusicPlayback() {
      const player = this.$refs.musicPlayer
      if (!player) return
      if (player.paused) {
        const audiobook = this.$refs.novelAudioPlayer
        if (audiobook && !audiobook.paused) {
          this.saveAudiobookProgress()
          audiobook.pause()
        }
        player.play().catch(() => this.handleMusicError())
      }
      else player.pause()
    },
    seekMusic(event) {
      const player = this.$refs.musicPlayer
      if (!player) return
      const next = Math.min(this.musicDuration || 0, Math.max(0, Number(event.target.value) || 0))
      player.currentTime = next
      this.musicCurrentTime = next
      this.emitMusicState()
    },
    seekMusicLyric(line) {
      const player = this.$refs.musicPlayer
      if (!player || !line) return
      const next = Math.min(this.musicDuration || Number.MAX_SAFE_INTEGER, Math.max(0, Number(line.time) || 0))
      player.currentTime = next
      this.musicCurrentTime = next
      this.emitMusicState()
    },
    scrollActiveLyric() {
      if (!this.musicPlayerVisible || this.activeLyricIndex < 0) return
      const container = this.$refs.musicLyrics
      const lines = this.$refs.musicLyricLines || []
      const line = Array.isArray(lines) ? lines[this.activeLyricIndex] : lines
      if (!container || !line) return
      const top = Math.max(0, line.offsetTop - container.clientHeight / 2 + line.clientHeight / 2)
      const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
      container.scrollTo({ top, behavior: reduced ? "auto" : "smooth" })
    },
    toggleMusicLoop() {
      this.musicLoop = !this.musicLoop
    },
    handleMusicEnded() {
      if (this.musicLoop) {
        const player = this.$refs.musicPlayer
        if (player) { player.currentTime = 0; player.play().catch(() => {}) }
      } else if (this.playingMusicNext) {
        this.openAdjacentMusic(this.playingMusicNext)
      } else {
        this.musicPlaying = false
        this.emitMusicState()
      }
    },
    handleMusicError() {
      this.musicPlaying = false
      this.emitMusicState()
      this.$notice.error(this.$t("musicUi.playFailed"))
    },
    openAdjacentMusic(target) {
      if (target && target.id) this.openMusic(target)
    },
    async toggleMusicFavorite(track) {
      if (!track) return
      const collected = !track.collected
      if (this.loggedIn) {
        try {
          const res = await updateMusicFavorite({ music_id: track.id, collected: collected ? 1 : 0 })
          this.syncMusicState(track.id, { collected: !!res.data.collected })
        } catch (error) { return }
      } else {
        const ids = this.readLocal("starlive_music_favorites", []).map(Number).filter(id => id !== Number(track.id))
        if (collected) ids.unshift(Number(track.id))
        this.writeLocal("starlive_music_favorites", ids)
        this.syncMusicState(track.id, { collected })
      }
      const libraryIndex = this.libraryMusic.findIndex(item => Number(item.id) === Number(track.id))
      if (collected && libraryIndex < 0) {
        this.libraryMusic.unshift(this.decorateMusic({ ...track, collected: true, content_type: "music" }))
        this.libraryMusicTotal += 1
      } else if (!collected && libraryIndex >= 0) {
        this.libraryMusic.splice(libraryIndex, 1)
        this.libraryMusicTotal = Math.max(0, this.libraryMusicTotal - 1)
      }
      this.$notice.success(this.$t(collected ? "musicUi.favoriteAdded" : "musicUi.favoriteRemoved"))
    },
    syncMusicState(id, update) {
      const lists = [this.musicList, this.libraryMusic, this.featured.hot_music || [], this.featured.recommend_list || [], this.searchResults.music || [], this.rankingList]
      lists.forEach(list => list.forEach(item => {
        if ((item.content_type === "music" || item.audio_url) && Number(item.id) === Number(id)) Object.assign(item, update)
      }))
      if (this.playingMusic && Number(this.playingMusic.id) === Number(id)) Object.assign(this.playingMusic, update)
    },
    emitMusicState() {
      if (!this.playingMusic) {
        this.$emit("music-state-change", null)
        return
      }
      this.$emit("music-state-change", {
        track: {
          id: Number(this.playingMusic.id),
          title: this.playingMusic.title,
          artist: this.playingMusic.artist,
          cover_url: this.playingMusic.cover_url
        },
        current_time: this.musicCurrentTime,
        duration: this.musicDuration || Number(this.playingMusic.duration) || 0,
        playing: this.musicPlaying,
        full_visible: this.musicPlayerVisible
      })
    },
    async openGame(game) {
      const id = Number(game && game.id ? game.id : game)
      if (!id) return
      try {
        const res = await getGameStart({ id })
        this.playingGame = this.decorateGame(res.data.game)
        this.gameLaunch = res.data.launch
        this.gamePlayerVisible = true
        this.searchVisible = false
        this.rankingVisible = false
        this.track("game_start_play", "game", id, this.activeChannel)
      } catch (error) { /* 请求封装统一提示。 */ }
    },
    closeGamePlayer() {
      this.gamePlayerVisible = false
      this.playingGame = null
      this.gameLaunch = null
    },
    async finishGame(result) {
      let progress = result
      if (this.loggedIn) {
        try {
          const res = await updateGameProgress(result)
          progress = res.data
        } catch (error) { return }
      } else {
        const history = this.readLocal("starlive_game_history", {})
        const old = history[result.game_id] || {}
        progress = {
          ...old,
          last_score: Number(result.score),
          best_score: Math.max(Number(result.score), Number(old.best_score || 0)),
          total_play_seconds: Number(old.total_play_seconds || 0) + Number(result.play_seconds || 0),
          play_sessions: Number(old.play_sessions || 0) + 1,
          completed_count: Number(old.completed_count || 0) + (Number(result.completed) ? 1 : 0)
        }
        history[result.game_id] = {
          ...progress,
          game_id: result.game_id,
          title: this.playingGame.title,
          cover_url: this.playingGame.cover_url,
          game_type: this.playingGame.game_type,
          internal_code: this.playingGame.internal_code,
          update_time: Date.now()
        }
        this.writeLocal("starlive_game_history", history)
      }
      this.syncGameState(result.game_id, { progress })
      this.$notice.success(this.$t("gameUi.scoreSaved", { score: this.$n(result.score) }))
      this.track("game_complete", "game", result.game_id, "player")
    },
    async toggleGameFavorite(game) {
      if (!game) return
      const collected = !game.collected
      if (this.loggedIn) {
        try {
          const res = await updateGameFavorite({ game_id: game.id, collected: collected ? 1 : 0 })
          this.syncGameState(game.id, { collected: !!res.data.collected })
        } catch (error) { return }
      } else {
        const ids = this.readLocal("starlive_game_favorites", []).map(Number).filter(id => id !== Number(game.id))
        if (collected) ids.unshift(Number(game.id))
        this.writeLocal("starlive_game_favorites", ids)
        this.syncGameState(game.id, { collected })
      }
      const libraryIndex = this.libraryGame.findIndex(item => Number(item.id) === Number(game.id))
      if (collected && libraryIndex < 0) {
        this.libraryGame.unshift(this.decorateGame({ ...game, collected: true, content_type: "game" }))
        this.libraryGameTotal += 1
      } else if (!collected && libraryIndex >= 0) {
        this.libraryGame.splice(libraryIndex, 1)
        this.libraryGameTotal = Math.max(0, this.libraryGameTotal - 1)
      }
      this.$notice.success(this.$t(collected ? "gameUi.favoriteAdded" : "gameUi.favoriteRemoved"))
    },
    syncGameState(id, update) {
      const lists = [this.gameList, this.libraryGame, this.featured.hot_games || [], this.featured.recommend_list || [], this.searchResults.games || [], this.rankingList]
      lists.forEach(list => list.forEach(item => {
        if ((item.content_type === "game" || item.game_type) && Number(item.id) === Number(id)) Object.assign(item, update)
      }))
      if (this.playingGame && Number(this.playingGame.id) === Number(id)) Object.assign(this.playingGame, update)
    },
    toggleAudiobookPanel() {
      if (!this.readerData || !this.readerData.chapter.audio_available) {
        this.$notice.warning(this.$t("audiobookUi.unavailable"))
        return
      }
      this.readerSettingsVisible = false
      this.audiobookVisible = !this.audiobookVisible
    },
    handleAudiobookLoaded() {
      const player = this.$refs.novelAudioPlayer
      if (!player || !this.readerData || !this.novelDetail) return
      const serverProgress = this.readerData.audio_progress
      const localProgress = this.readLocal("starlive_novel_audio_progress", {})[this.novelDetail.novel.id]
      const progress = this.loggedIn ? serverProgress : localProgress
      const duration = Number(player.duration || this.readerData.chapter.audio_duration || 0)
      const sameChapter = progress && Number(progress.chapter_id) === Number(this.readerData.chapter.id)
      const position = sameChapter ? Number(progress.position_second || 0) : 0
      const rate = sameChapter ? Number(progress.playback_rate || 1) : Number(this.readLocal("starlive_audiobook_rate", 1))
      this.audiobookDuration = duration
      this.audiobookTime = Math.min(Math.max(0, position), Math.max(0, duration - 1))
      this.audiobookRate = [1, 1.25, 1.5, 2].includes(rate) ? rate : 1
      player.currentTime = this.audiobookTime
      player.playbackRate = this.audiobookRate
      if (this.audiobookAutoplayPending) {
        this.audiobookAutoplayPending = false
        player.play().catch(() => this.handleAudiobookError())
      }
    },
    handleAudiobookTimeUpdate(event) {
      const player = event.target
      this.audiobookTime = Number(player.currentTime || 0)
      this.audiobookDuration = Number(player.duration || this.audiobookDuration || 0)
      if (Math.abs(this.audiobookTime - this.audiobookSaveSecond) >= 15) {
        this.audiobookSaveSecond = this.audiobookTime
        this.saveAudiobookProgress()
      }
    },
    toggleAudiobook() {
      const player = this.$refs.novelAudioPlayer
      if (!player) return
      const musicPlayer = this.$refs.musicPlayer
      if (musicPlayer && !musicPlayer.paused) musicPlayer.pause()
      if (player.paused) player.play().catch(() => this.handleAudiobookError())
      else player.pause()
    },
    seekAudiobook(event) {
      const player = this.$refs.novelAudioPlayer
      if (!player) return
      const position = Math.min(this.audiobookDuration || 0, Math.max(0, Number(event.target.value) || 0))
      player.currentTime = position
      this.audiobookTime = position
      this.saveAudiobookProgress()
    },
    cycleAudiobookRate() {
      const rates = [1, 1.25, 1.5, 2]
      const index = rates.indexOf(this.audiobookRate)
      this.audiobookRate = rates[(index + 1) % rates.length]
      const player = this.$refs.novelAudioPlayer
      if (player) player.playbackRate = this.audiobookRate
      this.writeLocal("starlive_audiobook_rate", this.audiobookRate)
      this.saveAudiobookProgress()
    },
    saveAudiobookProgress() {
      if (!this.readerData || !this.novelDetail || !this.readerData.chapter.audio_available) return
      const player = this.$refs.novelAudioPlayer
      const data = {
        novel_id: Number(this.novelDetail.novel.id),
        chapter_id: Number(this.readerData.chapter.id),
        chapter_no: Number(this.readerData.chapter.chapter_no),
        position_second: Math.floor(player ? player.currentTime : this.audiobookTime),
        duration: Math.floor(player ? (player.duration || this.audiobookDuration) : this.audiobookDuration),
        playback_rate: this.audiobookRate
      }
      if (this.loggedIn) {
        updateNovelAudioProgress(data).catch(() => {})
      } else {
        const progress = this.readLocal("starlive_novel_audio_progress", {})
        progress[data.novel_id] = { ...data, update_time: Date.now() }
        this.writeLocal("starlive_novel_audio_progress", progress)
      }
      this.readerData.audio_progress = data
    },
    openAudiobookChapter(target) {
      if (!target || !target.audio_available) return
      this.saveAudiobookProgress()
      this.openReader(target.id, true)
    },
    handleAudiobookEnded() {
      this.audiobookPlaying = false
      this.audiobookTime = this.audiobookDuration
      this.saveAudiobookProgress()
      if (this.readerData && this.readerData.next && this.readerData.next.audio_available) {
        this.openAudiobookChapter(this.readerData.next)
      }
    },
    handleAudiobookError() {
      this.audiobookPlaying = false
      this.audiobookAutoplayPending = false
      this.$notice.error(this.$t("audiobookUi.loadFailed"))
    },
    async openNovel(id, autoread = false) {
      try {
        const res = await getNovelDetail({ id })
        this.novelDetail = { ...res.data, novel: this.decorateNovel(res.data.novel) }
        this.chapterList = []
        this.chapterPage = 1
        this.chapterHasMore = false
        this.novelDetailVisible = true
        this.searchVisible = false
        this.rankingVisible = false
        this.track("click_novel", "novel", id, this.activeChannel)
        if (autoread) this.$nextTick(() => this.readNovel())
      } catch (error) { /* 请求封装统一提示。 */ }
    },
    readNovel() {
      const novel = this.novelDetail.novel
      const chapterId = novel.progress ? novel.progress.chapter_id : this.novelDetail.first_chapter && this.novelDetail.first_chapter.id
      if (chapterId) this.openReader(chapterId)
    },
    async openChapterDirectory() {
      this.readerSettingsVisible = false
      this.directoryVisible = true
      await this.loadChapters(true)
    },
    async loadChapters(reset = true) {
      if (!this.novelDetail || this.chapterLoading) return
      this.chapterLoading = true
      const page = reset ? 1 : this.chapterPage
      try {
        const res = await getNovelChapters({ novel_id: this.novelDetail.novel.id, page, limit: 50, order: this.chapterOrder })
        this.chapterList = reset ? res.data.list : this.chapterList.concat(res.data.list)
        this.chapterPage = page + 1
        this.chapterHasMore = !!res.data.has_more
      } catch (error) { /* 请求封装统一提示。 */ } finally {
        this.chapterLoading = false
      }
    },
    async loadMoreChapters() {
      await this.loadChapters(false)
    },
    async toggleChapterOrder() {
      this.chapterOrder = this.chapterOrder === "asc" ? "desc" : "asc"
      await this.loadChapters(true)
    },
    async openReader(chapterId, audiobookAutoplay = false) {
      if (!chapterId || !this.novelDetail) return
      const currentChapterId = this.readerData && this.readerData.chapter ? Number(this.readerData.chapter.id) : 0
      const changingChapter = currentChapterId > 0 && currentChapterId !== Number(chapterId)
      if (changingChapter) {
        this.saveNovelProgress()
        this.saveAudiobookProgress()
        const currentAudio = this.$refs.novelAudioPlayer
        if (currentAudio) currentAudio.pause()
      }
      try {
        const res = await getNovelChapter({ novel_id: this.novelDetail.novel.id, chapter_id: chapterId })
        if (!res.data.can_read) { this.$notice.warning(this.$t(`errors.${res.data.reason_code}`)); return }
        this.readerData = res.data
        this.readerVisible = true
        this.directoryVisible = false
        this.readerPercent = changingChapter ? 0 : Number(res.data.position_percent || 0)
        this.readerSavePercent = -1
        this.audiobookVisible = audiobookAutoplay && !!res.data.chapter.audio_available
        this.audiobookPlaying = false
        this.audiobookTime = 0
        this.audiobookDuration = Number(res.data.chapter.audio_duration || 0)
        this.audiobookSaveSecond = -1
        this.audiobookAutoplayPending = audiobookAutoplay && !!res.data.chapter.audio_available
        this.$nextTick(() => {
          const scroll = this.$refs.readerScroll
          if (!scroll) return
          const max = Math.max(0, scroll.scrollHeight - scroll.clientHeight)
          scroll.scrollTop = this.readerPercent > 0 ? max * this.readerPercent / 100 : 0
        })
        this.track("novel_start_read", "novel", this.novelDetail.novel.id, "reader")
      } catch (error) { /* 请求封装统一提示。 */ }
    },
    handleReaderScroll(event) {
      const element = event.target
      const max = element.scrollHeight - element.clientHeight
      this.readerPercent = max > 0 ? Math.min(100, Math.round(element.scrollTop / max * 100)) : 100
      if (Math.abs(this.readerPercent - this.readerSavePercent) >= 10) {
        this.readerSavePercent = this.readerPercent
        this.saveNovelProgress()
      }
    },
    saveNovelProgress() {
      if (!this.readerData || !this.novelDetail) return
      const data = { novel_id: this.novelDetail.novel.id, chapter_id: this.readerData.chapter.id, chapter_no: this.readerData.chapter.chapter_no, position_percent: this.readerPercent }
      if (this.loggedIn) {
        updateNovelProgress(data).catch(() => {})
      } else {
        // 阅读位置使用百分比保存，正文长度变化后仍能恢复到接近的位置。
        const history = this.readLocal("starlive_novel_history", {})
        history[data.novel_id] = { ...data, title: this.novelDetail.novel.title, cover_url: this.novelDetail.novel.cover_url, update_time: Date.now() }
        this.writeLocal("starlive_novel_history", history)
      }
      this.novelDetail.novel.progress = data
    },
    closeReader() {
      this.saveNovelProgress()
      this.saveAudiobookProgress()
      const audiobook = this.$refs.novelAudioPlayer
      if (audiobook) audiobook.pause()
      this.audiobookVisible = false
      this.audiobookPlaying = false
      this.audiobookAutoplayPending = false
      this.readerSettingsVisible = false
      this.readerVisible = false
      this.readerData = null
    },
    loadReaderPreferences() {
      const preferences = this.readLocal("starlive_reader_preferences", {})
      const fontSize = Number(preferences.fontSize)
      const lineHeight = Number(preferences.lineHeight)
      this.readerFontSize = Number.isFinite(fontSize) ? Math.min(24, Math.max(14, fontSize)) : 17
      this.readerLineHeight = [1.7, 2, 2.3].includes(lineHeight) ? lineHeight : 2
      this.readerTheme = ["paper", "eye", "night"].includes(preferences.theme) ? preferences.theme : "paper"
    },
    updateReaderPreference(field, value) {
      const currentPercent = this.readerPercent
      if (field === "fontSize") this.readerFontSize = Math.min(24, Math.max(14, Number(value) || 17))
      if (field === "lineHeight" && [1.7, 2, 2.3].includes(Number(value))) this.readerLineHeight = Number(value)
      if (field === "theme" && ["paper", "eye", "night"].includes(value)) this.readerTheme = value
      this.writeLocal("starlive_reader_preferences", {
        fontSize: this.readerFontSize,
        lineHeight: this.readerLineHeight,
        theme: this.readerTheme
      })
      this.$nextTick(() => window.requestAnimationFrame(() => {
        const scroll = this.$refs.readerScroll
        if (!scroll) return
        const max = Math.max(0, scroll.scrollHeight - scroll.clientHeight)
        scroll.scrollTop = max * currentPercent / 100
      }))
    },
    async toggleBookshelf(novel) {
      const next = !novel.collected
      if (this.loggedIn) {
        try {
          const res = await updateBookshelf({ novel_id: novel.id, collected: next ? 1 : 0 })
          novel.collected = res.data.collected
          this.syncNovelState(novel.id, "collected", novel.collected)
          this.$notice.success(this.$t("common.success"))
        } catch (error) { /* 请求封装统一提示。 */ }
      } else {
        let shelf = this.readLocal("starlive_novel_shelf", [])
        shelf = next ? Array.from(new Set(shelf.concat(Number(novel.id)))) : shelf.filter(id => Number(id) !== Number(novel.id))
        this.writeLocal("starlive_novel_shelf", shelf)
        novel.collected = next
        this.syncNovelState(novel.id, "collected", next)
        this.$notice.success(this.$t(next ? "discoverDetail.localAdded" : "discoverDetail.localRemoved"))
      }
      if (this.libraryVisible) {
        const shelfIndex = this.libraryShelf.findIndex(item => Number(item.id) === Number(novel.id))
        if (novel.collected && shelfIndex < 0) {
          this.libraryShelf.unshift(this.decorateNovel({ ...novel, collected: true }))
          this.libraryShelfTotal += 1
        } else if (!novel.collected && shelfIndex >= 0) {
          this.libraryShelf.splice(shelfIndex, 1)
          this.libraryShelfTotal = Math.max(0, this.libraryShelfTotal - 1)
        }
      }
      this.track(next ? "add_bookshelf" : "remove_bookshelf", "novel", novel.id, "novel")
    },
    syncNovelState(id, key, value) {
      const lists = [this.novelList, this.libraryShelf, this.featured.hot_novels || [], this.featured.recommend_list || [], this.searchResults.novels || []]
      lists.forEach(list => {
        const item = list.find(row => Number(row.id) === Number(id) && (!row.content_type || row.content_type === "novel"))
        if (item) this.$set(item, key, value)
      })
    },
    openSearch() {
      this.searchVisible = true
      this.searchSubmitted = false
      this.searchKeyword = ""
      this.searchResults = { dramas: [], novels: [], music: [], games: [] }
      this.$nextTick(() => { if (this.$refs.searchInput) this.$refs.searchInput.focus() })
    },
    closeSearch() { this.searchVisible = false },
    searchByWord(word) { this.searchKeyword = word; this.submitSearch() },
    changeSearchType(type) { this.searchType = type; this.submitSearch(false) },
    async submitSearch(save = true) {
      if (!this.searchKeyword) { this.$notice.warning(this.$t("discoverDetail.searchRequired")); return }
      if (save !== false) this.saveSearchHistory(this.searchKeyword)
      this.searchSubmitted = true
      this.searchLoading = true
      try {
        const res = await searchDiscover({ keyword: this.searchKeyword, type: this.searchType, page: 1, limit: 20 })
        this.searchResults = { dramas: res.data.dramas.map(this.decorateDrama), novels: res.data.novels.map(this.decorateNovel), music: (res.data.music || []).map(this.decorateMusic), games: (res.data.games || []).map(this.decorateGame) }
        this.track("search_keyword", "search", 0, this.searchType)
      } catch (error) {
        this.searchResults = { dramas: [], novels: [], music: [], games: [] }
      } finally {
        this.searchLoading = false
      }
    },
    saveSearchHistory(word) {
      this.searchHistory = [word].concat(this.searchHistory.filter(item => item !== word)).slice(0, 10)
      this.writeLocal("starlive_discover_searches", this.searchHistory)
    },
    removeSearchHistory(word) {
      this.searchHistory = this.searchHistory.filter(item => item !== word)
      this.writeLocal("starlive_discover_searches", this.searchHistory)
    },
    clearSearchHistory() { this.searchHistory = []; this.writeLocal("starlive_discover_searches", []) },
    async openRanking(type) {
      this.rankingType = type
      this.rankingTitle = this.rankingLabel(type)
      this.rankingVisible = true
      await this.loadRanking()
    },
    async loadRanking() {
      try {
        const res = await getRanking({ type: this.rankingType, limit: 20 })
        this.rankingList = res.data.list.map(item => this.rankingType.indexOf("game") === 0 ? this.decorateGame(item) : this.rankingType.indexOf("music") === 0 ? this.decorateMusic(item) : this.rankingType.indexOf("drama") === 0 ? this.decorateDrama(item) : this.decorateNovel(item))
        this.track("click_ranking", this.rankingType.indexOf("game") === 0 ? "game" : this.rankingType.indexOf("music") === 0 ? "music" : this.rankingType.indexOf("drama") === 0 ? "drama" : "novel", 0, this.rankingType)
      } catch (error) {
        this.rankingList = []
      }
    },
    openRankingItem(item) {
      if (this.rankingType.indexOf("game") === 0) this.openGame(item)
      else if (this.rankingType.indexOf("music") === 0) this.openMusic(item)
      else if (this.rankingType.indexOf("drama") === 0) this.openDrama(item.id)
      else this.openNovel(item.id)
    },
    openMixed(item) {
      if (item.content_type === "game") this.openGame(item)
      else if (item.content_type === "music") this.openMusic(item)
      else if (item.content_type === "drama") this.openDrama(item.id)
      else this.openNovel(item.id)
    },
    continueContent(item) {
      if (item.content_type === "drama") this.openDrama(item.id, true)
      else if (item.content_type === "game") this.openGame(item)
      else if (item.content_type === "music") this.openMusic(item)
      else this.openNovel(item.id, true)
    },
    async openLibrary(tab = "history") {
      this.libraryVisible = true
      this.searchVisible = false
      this.rankingVisible = false
      await this.switchLibraryTab(tab, true)
      this.track("open_content_library", tab === "history" ? "channel" : "novel", 0, tab)
    },
    async switchLibraryTab(tab, force = false) {
      this.libraryTab = tab
      if (force || !this.activeLibraryItems.length) await this.loadLibrary(true)
    },
    async loadLibrary(reset = true) {
      if (this.libraryLoading) return
      this.libraryLoading = true
      try {
        if (this.libraryTab === "history") await this.loadLibraryHistory(reset)
        else if (this.libraryTab === "bookshelf") await this.loadLibraryShelf(reset)
        else await this.loadLibraryFavorites(this.libraryTab, reset)
      } catch (error) {
        if (reset) this.resetActiveLibrary()
      } finally {
        this.libraryLoading = false
      }
    },
    async loadLibraryHistory(reset = true) {
      if (reset) this.libraryHistoryPage = 1
      if (this.loggedIn) {
        const res = await getContinueList({ page: this.libraryHistoryPage, limit: 20 })
        const next = res.data.list || []
        this.libraryHistory = reset ? next : this.uniqueLibraryItems(this.libraryHistory.concat(next))
        this.libraryHistoryTotal = Number(res.data.total || this.libraryHistory.length)
        this.libraryHistoryHasMore = !!res.data.has_more
        if (res.data.has_more) this.libraryHistoryPage += 1
        return
      }
      this.libraryHistory = this.localContinueItems(0)
      this.libraryHistoryTotal = this.libraryHistory.length
      this.libraryHistoryHasMore = false
    },
    async loadLibraryShelf(reset = true) {
      if (reset) this.libraryShelfPage = 1
      if (this.loggedIn) {
        const res = await getBookshelfList({ page: this.libraryShelfPage, limit: 20 })
        const next = (res.data.list || []).map(this.decorateNovel)
        this.libraryShelf = reset ? next : this.uniqueById(this.libraryShelf.concat(next))
        this.libraryShelfTotal = Number(res.data.total || this.libraryShelf.length)
        this.libraryShelfHasMore = !!res.data.has_more
        if (res.data.has_more) this.libraryShelfPage += 1
        return
      }
      const ids = this.readLocal("starlive_novel_shelf", []).map(Number)
      const rows = await Promise.all(ids.map(id => getNovelDetail({ id }).then(res => ({ ...this.decorateNovel(res.data.novel), collected: true })).catch(() => null)))
      this.libraryShelf = rows.filter(Boolean)
      this.libraryShelfTotal = this.libraryShelf.length
      this.libraryShelfHasMore = false
    },
    async loadLibraryFavorites(type, reset = true) {
      const isMusic = type === "music"
      const pageKey = isMusic ? "libraryMusicPage" : "libraryGamePage"
      const listKey = isMusic ? "libraryMusic" : "libraryGame"
      const totalKey = isMusic ? "libraryMusicTotal" : "libraryGameTotal"
      const moreKey = isMusic ? "libraryMusicHasMore" : "libraryGameHasMore"
      const decorate = isMusic ? this.decorateMusic : this.decorateGame
      if (reset) this[pageKey] = 1

      if (this.loggedIn) {
        const res = await getFavoriteList({ type, page: this[pageKey], limit: 20 })
        const next = (res.data.list || []).map(decorate)
        this[listKey] = reset ? next : this.uniqueById(this[listKey].concat(next))
        this[totalKey] = Number(res.data.total || this[listKey].length)
        this[moreKey] = !!res.data.has_more
        if (res.data.has_more) this[pageKey] += 1
        return
      }

      const storageKey = isMusic ? "starlive_music_favorites" : "starlive_game_favorites"
      const ids = this.readLocal(storageKey, []).map(Number)
      const requests = ids.map(id => {
        const request = isMusic ? getMusicDetail({ id }) : getGameDetail({ id })
        return request
          .then(res => decorate(isMusic ? res.data.track : res.data.game))
          .catch(() => null)
      })
      this[listKey] = (await Promise.all(requests)).filter(Boolean)
      this[totalKey] = this[listKey].length
      this[moreKey] = false
    },
    resetActiveLibrary() {
      const prefix = this.libraryTab === "history"
        ? "libraryHistory"
        : this.libraryTab === "bookshelf"
          ? "libraryShelf"
          : this.libraryTab === "music"
            ? "libraryMusic"
            : "libraryGame"
      this[prefix] = []
      this[`${prefix}Total`] = 0
      this[`${prefix}HasMore`] = false
    },
    loadMoreLibrary() {
      if (!this.activeLibraryHasMore) return
      this.loadLibrary(false)
    },
    deleteLibraryHistory(item) {
      this.$notice.confirm(this.$t("discoverLibrary.deleteConfirm"), this.$t("common.confirm"), { type: "warning" }).then(async () => {
        if (this.loggedIn) {
          await deleteContinueItem({ content_type: item.content_type, content_id: item.id })
        } else {
          const key = item.content_type === "game" ? "starlive_game_history" : item.content_type === "music" ? "starlive_music_history" : item.content_type === "drama" ? "starlive_drama_history" : "starlive_novel_history"
          const history = this.readLocal(key, {})
          delete history[item.id]
          this.writeLocal(key, history)
        }
        this.libraryHistory = this.libraryHistory.filter(row => !(row.content_type === item.content_type && Number(row.id) === Number(item.id)))
        this.libraryHistoryTotal = Math.max(0, this.libraryHistoryTotal - 1)
        if (this.featured.continue_list) this.featured.continue_list = this.featured.continue_list.filter(row => !(row.content_type === item.content_type && Number(row.id) === Number(item.id)))
        this.$notice.success(this.$t("discoverLibrary.deleted"))
      }).catch(() => {})
    },
    clearLibraryHistory() {
      this.$notice.confirm(this.$t("discoverLibrary.clearConfirm"), this.$t("common.confirm"), { type: "warning" }).then(async () => {
        if (this.loggedIn) await clearContinueList()
        else {
          this.writeLocal("starlive_drama_history", {})
          this.writeLocal("starlive_novel_history", {})
          this.writeLocal("starlive_music_history", {})
          this.writeLocal("starlive_game_history", {})
        }
        this.libraryHistory = []
        this.libraryHistoryTotal = 0
        this.libraryHistoryHasMore = false
        this.featured.continue_list = []
        this.$notice.success(this.$t("discoverLibrary.cleared"))
      }).catch(() => {})
    },
    removeLibraryShelf(novel) {
      this.$notice.confirm(this.$t("discoverLibrary.removeShelfConfirm", { title: novel.title }), this.$t("common.confirm"), { type: "warning" }).then(async () => {
        await this.toggleBookshelf(novel)
      }).catch(() => {})
    },
    async markNotInterested(item, index) {
      if (this.loggedIn) {
        try { await addDiscoveryFeedback({ content_type: item.content_type, content_id: item.id, source_page: "discover", source_module: "recommend" }) } catch (error) { return }
      }
      this.featured.recommend_list.splice(index, 1)
      this.$notice.success(this.$t("discoverDetail.lessRecommendations"))
      this.track("not_interested", item.content_type, item.id, "recommend")
    },
    localContinueItems(limit = 8) {
      const dramas = Object.values(this.readLocal("starlive_drama_history", {})).map(item => ({ ...item, id: item.drama_id, content_type: "drama", progress_text: this.$t("discoverUi.progressEpisode", { count: item.episode_no }), percent: item.duration ? Math.round(item.position_second / item.duration * 100) : 0 }))
      const novels = Object.values(this.readLocal("starlive_novel_history", {})).map(item => ({ ...item, id: item.novel_id, content_type: "novel", progress_text: this.$t("discoverUi.progressChapter", { count: item.chapter_no }), percent: item.position_percent }))
      const music = Object.values(this.readLocal("starlive_music_history", {})).map(item => ({ ...item, id: item.music_id, content_type: "music", percent: item.duration ? Math.round(item.position_second / item.duration * 100) : 0 }))
      const games = Object.values(this.readLocal("starlive_game_history", {})).map(item => ({ ...item, id: item.game_id, content_type: "game", percent: 0 }))
      const list = dramas.concat(novels, music, games).sort((left, right) => Number(right.update_time) - Number(left.update_time))
      return limit > 0 ? list.slice(0, limit) : list
    },
    track(eventName, contentType = "", contentId = 0, moduleName = "") {
      const deviceId = this.getDeviceId()
      addDiscoveryEvent({ event_name: eventName, content_type: contentType, content_id: contentId, page_name: "discover", channel_name: this.activeChannel, module_name: moduleName, device_id: deviceId, language: navigator.language || "zh-CN" }).catch(() => {})
    },
    getDeviceId() {
      let id = window.localStorage.getItem("starlive_device_id")
      if (!id) { id = `web-${Date.now()}-${Math.random().toString(16).slice(2)}`; window.localStorage.setItem("starlive_device_id", id) }
      return id
    },
    readLocal(key, fallback) {
      try { const value = JSON.parse(window.localStorage.getItem(key)); return value === null ? fallback : value } catch (error) { return fallback }
    },
    writeLocal(key, value) { window.localStorage.setItem(key, JSON.stringify(value)) },
    uniqueById(list) {
      const ids = new Set()
      return list.filter(item => { if (ids.has(Number(item.id))) return false; ids.add(Number(item.id)); return true })
    },
    uniqueLibraryItems(list) {
      const ids = new Set()
      return list.filter(item => {
        const key = `${item.content_type}-${Number(item.id)}`
        if (ids.has(key)) return false
        ids.add(key)
        return true
      })
    },
    formatHeat(value) {
      return this.$compact(value)
    },
    formatWords(value) {
      return this.$compact(value)
    },
    hideBrokenImage(event) { event.target.style.display = "none" }
  },
  mounted() { this.loadReaderPreferences(); this.initialize() },
  beforeDestroy() {
    window.clearInterval(this.bannerTimer)
    const audiobook = this.$refs.novelAudioPlayer
    if (audiobook && this.readerData) {
      this.saveAudiobookProgress()
      audiobook.pause()
    }
    const musicPlayer = this.$refs.musicPlayer
    if (musicPlayer && this.playingMusic) {
      this.saveMusicProgress(Math.floor(musicPlayer.currentTime), Math.floor(musicPlayer.duration || this.playingMusic.duration))
      musicPlayer.pause()
    }
    if (this.gamePlayerVisible) this.closeGamePlayer()
    this.track("leave_discover", "channel", 0, this.activeChannel)
  }
}
</script>

<style>
.discover-page { --ink:#08090d; --panel:#111319; --line:rgba(255,255,255,.09); --muted:rgba(255,255,255,.56); --coral:#ff4d67; --cyan:#35e4d4; position:absolute; z-index:12; inset:0 0 62px; overflow:hidden; color:#f8f8f8; background:radial-gradient(circle at 90% 0,rgba(53,228,212,.09),transparent 32%),linear-gradient(180deg,#0b0c11,#08090d 38%); font-family:"Microsoft YaHei UI","PingFang SC",sans-serif; }
.discover-page button { border:0; color:inherit; background:none; font:inherit; cursor:pointer; }
.discover-navigation { height:calc(54px + env(safe-area-inset-top)); display:flex; align-items:flex-end; gap:7px; padding:env(safe-area-inset-top) 10px 0 0; border-bottom:1px solid var(--line); }
.discover-channel-arrow { width:32px; height:38px; display:grid; place-items:center; flex:none; margin-bottom:8px; border:1px solid var(--line)!important; border-radius:12px; color:#d9dae0!important; background:rgba(255,255,255,.04)!important; font-size:13px!important; transition:border-color .2s ease,color .2s ease,opacity .2s ease; }
.discover-channel-arrow:not(:disabled):hover,.discover-channel-arrow:not(:disabled):focus-visible { border-color:rgba(255,77,103,.48)!important; color:#fff!important; }
.discover-channel-arrow:disabled { opacity:.2; cursor:default; }
[dir="rtl"] .discover-channel-arrow i { transform:scaleX(-1); }
.discover-tabs { min-width:0; height:54px; display:flex; flex:1; gap:26px; align-items:center; overflow-x:auto; overflow-y:hidden; padding:0 6px; scroll-behavior:smooth; scroll-padding-inline:6px; scroll-snap-type:x proximity; scrollbar-width:none; overscroll-behavior-inline:contain; touch-action:pan-x; -webkit-overflow-scrolling:touch; }
.discover-tabs::-webkit-scrollbar { display:none; }
.discover-tabs button { position:relative; height:54px; max-width:none; flex:0 0 auto; padding-inline:2px; overflow:visible; color:var(--muted); font-size:14px; white-space:nowrap; scroll-snap-align:center; }
.discover-tabs button.active { color:#fff; font-weight:800; }
.discover-tabs button.active::after { content:""; position:absolute; left:20%; right:20%; bottom:0; height:3px; border-radius:3px; background:var(--coral); box-shadow:0 0 12px rgba(255,77,103,.7); }
.discover-search { width:40px; height:40px; display:grid; place-items:center; flex:none; margin:0 6px 7px; border:1px solid var(--line)!important; border-radius:14px; background:rgba(255,255,255,.045)!important; font-size:18px!important; }
.discover-scroll,.overlay-scroll { height:calc(100% - 54px - env(safe-area-inset-top)); overflow-y:auto; overscroll-behavior:contain; scrollbar-width:none; }
.discover-scroll::-webkit-scrollbar,.overlay-scroll::-webkit-scrollbar { display:none; }
.discover-hero { position:relative; padding:14px 16px 6px; }
.hero-card { position:relative; width:100%; height:176px; overflow:hidden; display:block; padding:0; border-radius:16px; text-align:left; background:#171a21; box-shadow:0 20px 45px rgba(0,0,0,.28); }
.hero-card img { width:100%; height:100%; object-fit:cover; }
.hero-shade { position:absolute; inset:0; background:linear-gradient(90deg,rgba(5,6,9,.94),rgba(5,6,9,.38) 65%,rgba(5,6,9,.08)),linear-gradient(0deg,rgba(5,6,9,.7),transparent 55%); }
.hero-badge { position:absolute; right:12px; top:12px; padding:4px 7px; border-radius:5px; background:var(--coral); font-size:8px; font-weight:900; }
.hero-card > div { position:absolute; left:16px; right:56px; bottom:15px; }
.hero-card > div small { color:var(--cyan); font-size:7px; font-weight:900; letter-spacing:2px; }
.hero-card h2 { max-width:250px; margin:6px 0 3px; font-size:21px; line-height:1.18; }
.hero-card p { overflow:hidden; margin:0 0 10px; color:rgba(255,255,255,.72); font-size:10px; white-space:nowrap; text-overflow:ellipsis; }
.hero-card em { padding:6px 10px; border-radius:15px; color:#090a0e; background:#fff; font-size:9px; font-style:normal; font-weight:800; }
.hero-dots { position:absolute; right:27px; bottom:17px; display:flex; gap:4px; }
.hero-dots button { width:5px; height:5px; padding:0; border-radius:5px; background:rgba(255,255,255,.38); }
.hero-dots button.active { width:15px; background:#fff; }
.discover-shortcuts { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; padding:12px 16px 4px; }
.discover-shortcuts button { position:relative; display:flex; flex-direction:column; align-items:center; gap:6px; padding:10px 4px; border:1px solid var(--line); border-radius:13px; background:rgba(255,255,255,.035); }
.discover-shortcuts button > i { width:34px; height:34px; display:grid; place-items:center; border-radius:11px; color:#08090d; background:var(--cyan); font-size:17px; box-shadow:0 7px 20px rgba(53,228,212,.18); }
.discover-shortcuts button:nth-child(even) > i { background:#ff7386; }
.discover-shortcuts span { font-size:10px; font-weight:700; }
.discover-shortcuts em { position:absolute; right:7px; top:6px; min-width:14px; height:14px; display:grid; place-items:center; padding:0 3px; border-radius:7px; color:#fff; background:var(--coral); font-size:7px; font-style:normal; }
.discover-module { padding:19px 16px 0; }
.module-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:11px; }
.module-head h2 { margin:0; font-size:17px; letter-spacing:-.5px; }
.module-head button { color:var(--muted); font-size:10px; }
.continue-rail,.poster-rail { display:flex; gap:10px; overflow-x:auto; padding-bottom:2px; scrollbar-width:none; }
.continue-rail::-webkit-scrollbar,.poster-rail::-webkit-scrollbar { display:none; }
.continue-rail > button { flex:0 0 228px; display:flex; gap:11px; padding:9px; border:1px solid var(--line); border-radius:13px; background:linear-gradient(135deg,rgba(255,255,255,.07),rgba(255,255,255,.025)); text-align:left; }
.continue-rail img { width:60px; height:76px; border-radius:9px; object-fit:cover; background:#1a1c23; }
.continue-rail span { min-width:0; flex:1; display:flex; flex-direction:column; justify-content:center; }
.continue-rail small { color:var(--cyan); font-size:8px; font-weight:800; }
.continue-rail strong { overflow:hidden; margin:5px 0; font-size:12px; white-space:nowrap; text-overflow:ellipsis; }
.continue-rail em { color:var(--muted); font-size:9px; font-style:normal; }
.continue-rail span > i { height:2px; margin-top:9px; border-radius:2px; background:rgba(255,255,255,.1); }
.continue-rail span > i b { display:block; height:100%; border-radius:2px; background:var(--coral); }
.poster-rail > button { flex:0 0 115px; min-width:0; padding:0; text-align:left; }
.poster { position:relative; height:154px; display:block; overflow:hidden; border-radius:11px; background:#171a21; }
.poster img { width:100%; height:100%; object-fit:cover; }
.poster > i { position:absolute; left:9px; bottom:9px; width:28px; height:28px; display:grid; place-items:center; border-radius:50%; color:#090a0e; background:rgba(255,255,255,.92); font-size:15px; }
.poster > em { position:absolute; left:7px; top:7px; padding:3px 5px; border-radius:4px; color:#fff; background:var(--coral); font-size:7px; font-style:normal; font-weight:800; }
.poster > b,.drama-grid span > b,.novel-cover > b { position:absolute; left:0; bottom:0; height:3px; background:var(--coral); }
.poster-rail > button > strong { display:-webkit-box; overflow:hidden; margin:7px 0 3px; font-size:11px; line-height:1.35; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
.poster-rail > button > small { display:block; overflow:hidden; color:var(--muted); font-size:8px; white-space:nowrap; text-overflow:ellipsis; }
.novel-list { display:flex; flex-direction:column; gap:11px; }
.novel-card { position:relative; min-height:112px; display:flex; padding:8px; border:1px solid var(--line); border-radius:13px; background:rgba(255,255,255,.035); }
.novel-main { min-width:0; flex:1; display:flex; gap:11px; padding:0; text-align:left; }
.novel-cover { position:relative; flex:0 0 67px; height:94px; overflow:hidden; border-radius:8px; background:#1b1d24; }
.novel-cover img { width:100%; height:100%; object-fit:cover; }
.novel-cover em { position:absolute; left:4px; top:4px; padding:2px 4px; border-radius:3px; color:#08090d; background:var(--cyan); font-size:6px; font-style:normal; font-weight:900; }
.novel-copy { min-width:0; flex:1; display:block; padding-top:2px; }
.novel-copy > strong { display:block; padding-right:30px; font-size:13px; }
.novel-copy > small { display:block; margin:3px 0; color:var(--cyan); font-size:8px; }
.novel-copy > p { display:-webkit-box; overflow:hidden; margin:5px 0; color:rgba(255,255,255,.58); font-size:9px; line-height:1.55; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
.novel-copy > em,.novel-copy > i { display:block; overflow:hidden; color:rgba(255,255,255,.42); font-size:8px; font-style:normal; white-space:nowrap; text-overflow:ellipsis; }
.novel-copy > i { margin-top:4px; color:rgba(255,255,255,.64); }
.novel-collect { position:absolute; right:8px; top:8px; width:28px; height:28px; display:grid; place-items:center; color:var(--muted); font-size:16px; }
.novel-collect.active { color:#ffd85c; }
.ranking-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:9px; }
.ranking-grid > button { min-height:138px; display:flex; flex-direction:column; align-items:flex-start; padding:12px; border:1px solid var(--line); border-radius:13px; background:linear-gradient(145deg,#181b23,#0d0f14); text-align:left; }
.ranking-grid > button:nth-child(2n) { background:linear-gradient(145deg,rgba(53,228,212,.15),#0d0f14 55%); }
.ranking-grid small { color:var(--coral); font-size:7px; font-weight:900; letter-spacing:1.5px; }
.ranking-grid strong { margin:4px 0 8px; font-size:12px; }
.ranking-grid span { width:100%; overflow:hidden; margin:2px 0; color:rgba(255,255,255,.68); font-size:8px; white-space:nowrap; text-overflow:ellipsis; }
.ranking-grid span b { margin-right:6px; color:var(--cyan); }
.ranking-grid em { margin-top:auto; color:var(--muted); font-size:8px; font-style:normal; }
.mixed-card { position:relative; padding:8px; border-top:1px solid var(--line); }
.mixed-main { width:100%; display:flex; gap:11px; padding:0 25px 0 0; text-align:left; }
.mixed-main img { flex:0 0 76px; width:76px; height:94px; border-radius:9px; object-fit:cover; background:#1a1c23; }
.mixed-main > span { min-width:0; flex:1; }
.mixed-main small { display:inline-block; padding:2px 5px; border-radius:4px; color:#08090d; background:var(--cyan); font-size:7px; font-weight:900; }
.mixed-main strong { display:block; margin:5px 0; font-size:12px; }
.mixed-main p { display:-webkit-box; overflow:hidden; margin:0 0 6px; color:var(--muted); font-size:9px; line-height:1.55; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
.mixed-main em { color:rgba(255,255,255,.42); font-size:8px; font-style:normal; }
.mixed-more { position:absolute; right:4px; top:10px; width:28px; height:28px; color:var(--muted); }
.category-rail { position:sticky; z-index:4; top:0; display:flex; gap:18px; overflow-x:auto; padding:11px 16px; background:rgba(8,9,13,.94); backdrop-filter:blur(16px); scrollbar-width:none; }
.category-rail::-webkit-scrollbar { display:none; }
.category-rail button { flex:0 0 auto; color:var(--muted); font-size:11px; }
.category-rail button.active { color:#fff; font-weight:800; }
.channel-feature { position:relative; height:195px; margin:3px 16px 0; overflow:hidden; border-radius:15px; background:#181a20; }
.channel-feature > img { width:100%; height:100%; object-fit:cover; }
.channel-feature-shade { position:absolute; inset:0; background:linear-gradient(90deg,rgba(7,8,12,.94),rgba(7,8,12,.28)),linear-gradient(0deg,rgba(7,8,12,.7),transparent); }
.channel-feature > div { position:absolute; left:15px; right:80px; bottom:14px; }
.channel-feature small,.novel-feature small { color:var(--coral); font-size:7px; font-weight:900; letter-spacing:1.8px; }
.channel-feature h2,.novel-feature h2 { margin:5px 0; font-size:19px; }
.channel-feature p { display:-webkit-box; overflow:hidden; margin:0 0 6px; color:rgba(255,255,255,.68); font-size:9px; line-height:1.5; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
.channel-feature em { display:block; margin-bottom:9px; color:var(--muted); font-size:8px; font-style:normal; }
.channel-feature button,.novel-feature button { padding:7px 12px; border-radius:16px; color:#08090d; background:#fff; font-size:9px; font-weight:800; }
.drama-channel-module,.novel-channel-module { padding-top:17px; }
.drama-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px 10px; }
.drama-grid > button { min-width:0; padding:0; text-align:left; }
.drama-grid > button > span { position:relative; height:205px; display:block; overflow:hidden; border-radius:11px; background:#171920; }
.drama-grid img { width:100%; height:100%; object-fit:cover; }
.drama-grid span > em { position:absolute; left:7px; top:7px; padding:3px 5px; border-radius:4px; background:var(--coral); font-size:7px; font-style:normal; font-weight:800; }
.drama-grid span > i { position:absolute; right:8px; bottom:8px; width:27px; height:27px; display:grid; place-items:center; border-radius:50%; color:#08090d; background:rgba(255,255,255,.9); }
.drama-grid > button > strong { display:block; overflow:hidden; margin:7px 0 3px; font-size:11px; white-space:nowrap; text-overflow:ellipsis; }
.drama-grid > button > small { display:block; overflow:hidden; color:var(--muted); font-size:8px; white-space:nowrap; text-overflow:ellipsis; }
.novel-feature { display:flex; gap:14px; margin:6px 16px 14px; padding:13px; border:1px solid var(--line); border-radius:15px; background:linear-gradient(135deg,rgba(255,77,103,.13),rgba(255,255,255,.03)); }
.novel-feature > img { width:92px; height:129px; flex:0 0 92px; border-radius:9px; object-fit:cover; box-shadow:0 14px 28px rgba(0,0,0,.35); }
.novel-feature > div { min-width:0; flex:1; }
.novel-feature h2 { font-size:17px; }
.novel-feature em { color:var(--cyan); font-size:9px; font-style:normal; }
.novel-feature p { display:-webkit-box; overflow:hidden; margin:6px 0; color:var(--muted); font-size:9px; line-height:1.55; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
.novel-feature span { display:block; margin-bottom:9px; color:rgba(255,255,255,.44); font-size:8px; }
.novel-rank-actions { display:grid; grid-template-columns:repeat(4,1fr); gap:6px; padding:0 16px; }
.novel-rank-actions button { padding:9px 2px; border:1px solid var(--line); border-radius:9px; color:rgba(255,255,255,.72); background:rgba(255,255,255,.03); font-size:9px; }
.list-status { margin:18px 0 0; color:var(--muted); font-size:9px; text-align:center; }
.discover-refresh { display:block; margin:24px auto 28px; padding:8px 13px; border:1px solid var(--line); border-radius:18px; color:var(--muted); font-size:9px; }
.discover-state { height:70%; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:30px; text-align:center; }
.discover-state > i { margin-bottom:12px; color:var(--coral); font-size:35px; }
.discover-state strong { font-size:14px; }
.discover-state p { margin:7px 0 16px; color:var(--muted); font-size:10px; }
.discover-state button { padding:8px 15px; border-radius:18px; color:#08090d; background:#fff; font-size:10px; font-weight:800; }
.discover-state.compact { height:220px; }
.discover-skeleton { padding:15px 16px; }
.discover-skeleton i { display:block; border-radius:12px; background:linear-gradient(90deg,#15171d,#232630,#15171d); background-size:200% 100%; animation:skeletonMove 1.2s linear infinite; }
.skeleton-hero { height:176px; }
.skeleton-shortcuts { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin:13px 0 22px; }
.skeleton-shortcuts i { height:70px; }
.skeleton-row { height:88px; margin:10px 0; }
.discover-overlay { position:absolute; z-index:30; inset:0; background:var(--ink); }
.directory-overlay { z-index:85; }
.overlay-header { height:56px; display:grid; grid-template-columns:44px 1fr 44px; align-items:center; padding-top:env(safe-area-inset-top); border-bottom:1px solid var(--line); }
.overlay-header > button { height:44px; font-size:17px; }
.overlay-header > strong { font-size:13px; text-align:center; }
.discover-overlay > .overlay-scroll { height:calc(100% - 56px - env(safe-area-inset-top)); }
.search-overlay > header { height:60px; display:grid; grid-template-columns:34px 1fr 38px; gap:7px; align-items:center; padding:env(safe-area-inset-top) 12px 0; }
.search-overlay > header > button { font-size:11px; }
.search-overlay > header > button:first-child { font-size:18px; }
.search-overlay > header > div { height:36px; display:flex; align-items:center; gap:7px; padding:0 10px; border-radius:18px; background:#181a21; }
.search-overlay input { min-width:0; flex:1; border:0; outline:0; color:#fff; background:transparent; font-size:11px; }
.search-overlay input::placeholder { color:rgba(255,255,255,.35); }
.search-type-tabs { height:42px; display:flex; gap:25px; align-items:center; padding:0 16px; border-bottom:1px solid var(--line); }
.search-type-tabs button { height:42px; color:var(--muted); font-size:11px; }
.search-type-tabs button.active { border-bottom:2px solid var(--coral); color:#fff; font-weight:800; }
.search-overlay .overlay-scroll { height:calc(100% - 60px); padding:0 16px 30px; }
.search-overlay .search-type-tabs + .overlay-scroll { height:calc(100% - 102px); }
.search-block { padding:22px 0 0; }
.search-block > div { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
.search-block h3,.search-result-block h3 { margin:0; font-size:15px; }
.search-block > div button { color:var(--muted); font-size:9px; }
.keyword-cloud { display:flex; flex-wrap:wrap; gap:7px; }
.keyword-cloud > button { padding:7px 10px; border-radius:15px; color:rgba(255,255,255,.75); background:#181a21; font-size:9px; }
.keyword-cloud i { margin-left:5px; color:rgba(255,255,255,.35); }
.hot-keywords { display:grid; grid-template-columns:repeat(2,1fr); gap:0 16px; margin:0; padding:0; list-style:none; }
.hot-keywords button { width:100%; display:flex; align-items:center; gap:8px; padding:9px 0; border-bottom:1px solid var(--line); text-align:left; }
.hot-keywords b { width:18px; color:var(--coral); font-family:Georgia,serif; font-size:15px; font-style:italic; }
.hot-keywords span { min-width:0; flex:1; overflow:hidden; font-size:10px; white-space:nowrap; text-overflow:ellipsis; }
.hot-keywords em { padding:2px 4px; border-radius:3px; color:#fff; background:var(--coral); font-size:7px; font-style:normal; }
.search-result-block { padding-top:20px; }
.search-result-block h3 { margin-bottom:12px; }
.search-result-block h3 small { color:var(--muted); font-size:8px; font-weight:400; }
.library-tabs { height:48px; display:flex; overflow-x:auto; padding:0 12px; border-bottom:1px solid var(--line); scrollbar-width:none; }
.library-tabs::-webkit-scrollbar { display:none; }
.library-tabs button { position:relative; min-width:94px; flex:1 0 auto; padding:0 7px; color:var(--muted); font-size:10px; white-space:nowrap; }
.library-tabs button.active { color:#fff; font-weight:800; }
.library-tabs button.active::after { content:""; position:absolute; left:30%; right:30%; bottom:0; height:3px; border-radius:3px; background:var(--coral); }
.library-tabs small { margin-left:4px; color:inherit; font-size:8px; }
.library-overlay .library-scroll { height:calc(100% - 104px - env(safe-area-inset-top)); padding:8px 16px 30px; }
.library-row { position:relative; display:flex; align-items:center; min-width:0; border-bottom:1px solid var(--line); }
.library-main { min-width:0; flex:1; display:flex; gap:12px; align-items:center; padding:12px 38px 12px 0; text-align:left; }
.library-cover { position:relative; width:66px; height:88px; flex:0 0 66px; overflow:hidden; border-radius:10px; background:#171920; }
.library-cover img { width:100%; height:100%; object-fit:cover; }
.library-cover > i { position:absolute; right:5px; bottom:5px; width:22px; height:22px; display:grid; place-items:center; border-radius:50%; color:#08090d; background:rgba(255,255,255,.9); font-size:11px; }
.library-copy { min-width:0; flex:1; }
.library-copy > small,.library-copy > strong,.library-copy > em,.library-copy > span { display:block; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.library-copy > small { color:var(--cyan); font-size:8px; }
.library-copy > strong { margin:5px 0; font-size:13px; }
.library-copy > em { color:var(--muted); font-size:8px; font-style:normal; }
.library-copy > i { display:block; height:3px; margin:9px 0; overflow:hidden; border-radius:3px; background:rgba(255,255,255,.1); }
.library-copy > i b { display:block; height:100%; border-radius:3px; background:linear-gradient(90deg,var(--coral),#ff9f62); }
.library-copy > span { color:#fff; font-size:9px; font-weight:800; }
.library-remove { position:absolute; right:0; width:34px; height:42px; display:grid; place-items:center; border-radius:17px; color:var(--muted)!important; background:#16181e!important; }
.shelf-row .library-main,.favorite-row .library-main { padding-right:94px; }
.shelf-row .library-remove,.favorite-row .library-remove { top:10px; color:#ffb24d!important; }
.library-continue { position:absolute; right:0; bottom:12px; padding:7px 10px!important; border-radius:15px; color:#08090d!important; background:#fff!important; font-size:8px!important; font-weight:800!important; }
.library-load-more { display:block; width:100%; margin:16px 0 0; padding:11px; border:1px solid var(--line)!important; border-radius:20px; color:#fff; background:#15171d!important; font-size:10px!important; }
[dir="rtl"] .library-main { padding-right:0; padding-left:38px; text-align:right; }
[dir="rtl"] .shelf-row .library-main,[dir="rtl"] .favorite-row .library-main { padding-right:0; padding-left:94px; }
[dir="rtl"] .shelf-row .library-main { padding-right:0; padding-left:94px; }
[dir="rtl"] .library-remove { right:auto; left:0; }
[dir="rtl"] .library-continue { right:auto; left:0; }
[dir="rtl"] .library-tabs small { margin-right:4px; margin-left:0; }
.drama-detail-hero { position:relative; height:330px; background:#16181e; }
.drama-detail-hero > img { width:100%; height:100%; object-fit:cover; }
.drama-detail-hero > span { position:absolute; inset:0; background:linear-gradient(0deg,#08090d 2%,rgba(8,9,13,.82) 26%,transparent 72%); }
.drama-detail-hero > div { position:absolute; left:16px; right:16px; bottom:8px; }
.drama-detail-hero em,.book-hero em { display:inline-block; padding:3px 6px; border-radius:4px; color:#fff; background:var(--coral); font-size:7px; font-style:normal; font-weight:900; }
.drama-detail-hero h1 { margin:7px 0; font-size:24px; letter-spacing:-1px; }
.drama-detail-hero p { display:-webkit-box; overflow:hidden; margin:0 0 6px; color:rgba(255,255,255,.72); font-size:10px; line-height:1.55; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
.drama-detail-hero small { display:block; color:var(--muted); font-size:8px; }
.drama-detail-hero button { margin-top:11px; padding:9px 15px; border-radius:18px; color:#08090d; background:#fff; font-size:10px; font-weight:800; }
.detail-section { padding:21px 16px 0; }
.detail-section > h3,.detail-title h3,.book-description h3 { margin:0 0 12px; font-size:15px; }
.detail-title { display:flex; align-items:center; justify-content:space-between; }
.detail-title span,.detail-title button { color:var(--muted); font-size:9px; }
.episode-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:8px; }
.episode-grid button { height:52px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:3px; border:1px solid var(--line); border-radius:9px; background:#12141a; }
.episode-grid button.watched { border-color:rgba(255,77,103,.45); background:rgba(255,77,103,.1); }
.episode-grid strong { font-size:13px; }
.episode-grid small,.episode-grid i { color:var(--muted); font-size:7px; }
.compact-posters { padding-bottom:25px; }
.book-poster { height:150px; }
.drama-player { position:absolute; z-index:60; inset:0; display:flex; align-items:center; justify-content:center; overflow:hidden; background:#000; }
.drama-player video { width:100%; height:100%; object-fit:contain; }
.player-shade { position:absolute; inset:0; pointer-events:none; background:linear-gradient(0deg,rgba(0,0,0,.72),transparent 24%,transparent 76%,rgba(0,0,0,.7)); }
.drama-player header { position:absolute; left:0; right:0; top:0; display:grid; grid-template-columns:45px 1fr 45px; align-items:center; padding:calc(10px + env(safe-area-inset-top)) 8px 10px; }
.drama-player header button { height:40px; font-size:18px; }
.drama-player header div { min-width:0; }
.drama-player header small,.drama-player header strong { display:block; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.drama-player header small { color:var(--muted); font-size:8px; }
.drama-player header strong { margin-top:3px; font-size:11px; }
.player-play { position:absolute; left:50%; top:50%; width:58px; height:58px; display:grid; place-items:center; transform:translate(-50%,-50%); border:1px solid rgba(255,255,255,.24); border-radius:50%; background:rgba(8,10,14,.58); backdrop-filter:blur(12px); font-size:25px; box-shadow:0 12px 36px rgba(0,0,0,.32); }
.player-bottom { position:absolute; z-index:2; left:16px; right:16px; bottom:calc(14px + env(safe-area-inset-bottom)); display:grid; gap:13px; }
.player-progress { display:grid; grid-template-columns:34px minmax(0,1fr) 34px; gap:9px; align-items:center; color:rgba(255,255,255,.8); font-size:8px; font-variant-numeric:tabular-nums; }
.player-progress span:last-child { text-align:right; }
.player-progress input { --player-progress:0%; position:relative; z-index:3; width:100%; height:30px; margin:0; appearance:none; background:transparent; cursor:pointer; touch-action:none; }
.player-progress input::-webkit-slider-runnable-track { height:3px; border-radius:3px; background:linear-gradient(90deg,var(--coral) 0 var(--player-progress),rgba(255,255,255,.28) var(--player-progress) 100%); }
.player-progress input::-webkit-slider-thumb { width:12px; height:12px; margin-top:-4.5px; appearance:none; border:2px solid #fff; border-radius:50%; background:var(--coral); box-shadow:0 2px 8px rgba(0,0,0,.45); }
.player-progress input::-moz-range-track { height:3px; border-radius:3px; background:rgba(255,255,255,.28); }
.player-progress input::-moz-range-progress { height:3px; border-radius:3px; background:var(--coral); }
.player-progress input::-moz-range-thumb { width:9px; height:9px; border:2px solid #fff; border-radius:50%; background:var(--coral); }
.drama-player footer { display:flex; align-items:center; justify-content:space-between; min-height:35px; padding:0 2px; }
.drama-player footer button { padding:8px 13px; border:1px solid rgba(255,255,255,.25); border-radius:17px; font-size:9px; }
.drama-player footer button:disabled { opacity:.35; }
.drama-player footer span { font-size:9px; }
.discover-live-transition { position:fixed;z-index:3004;inset:0;overflow:hidden;color:#fff;background:#050609;isolation:isolate; }
.discover-live-card-main { position:absolute;z-index:1;inset:0;width:100%;height:100%;overflow:hidden;padding:0!important;text-align:start;background:#08090d!important; }
.discover-live-card-backdrop { position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transform:scale(1.015);animation:discover-live-card-arrive .65s cubic-bezier(.2,.76,.26,1) both; }
.discover-live-card-shade { position:absolute;inset:0;background:linear-gradient(180deg,rgba(3,4,7,.62),rgba(3,4,7,.05) 35%,rgba(3,4,7,.22) 57%,rgba(3,4,7,.96) 100%),radial-gradient(circle at 70% 22%,rgba(255,77,103,.18),transparent 38%); }
.discover-live-card-copy { position:absolute;z-index:2;inset-inline:20px;bottom:calc(34px + env(safe-area-inset-bottom));display:flex;flex-direction:column;align-items:flex-start;pointer-events:none;text-shadow:0 3px 18px rgba(0,0,0,.68); }
.discover-live-card-copy > em { display:flex;align-items:center;gap:7px;padding:6px 10px;border-radius:999px;color:#fff;background:rgba(255,49,88,.9);box-shadow:0 8px 26px rgba(255,49,88,.3);font-size:9px;font-style:normal;font-weight:900;letter-spacing:1px; }
.discover-live-card-copy > em i { width:6px;height:6px;border-radius:50%;background:#fff;box-shadow:0 0 0 4px rgba(255,255,255,.18);animation:livePulse 1.3s infinite; }
.discover-live-card-author { max-width:100%;display:flex;align-items:center;gap:10px;margin-top:16px; }
.discover-live-card-author > img,.discover-live-card-author > b { width:43px;height:43px;display:grid;place-items:center;flex:0 0 43px;border:2px solid #fff;border-radius:50%;object-fit:cover;background:linear-gradient(135deg,#20232d,#ff6177);box-shadow:0 8px 24px rgba(0,0,0,.35);font-size:12px; }
.discover-live-card-author > span { min-width:0;display:grid;gap:2px; }.discover-live-card-author small { color:rgba(255,255,255,.66);font-size:9px; }.discover-live-card-author strong { overflow:hidden;font-size:13px;text-overflow:ellipsis;white-space:nowrap; }
.discover-live-card-copy h2 { max-width:min(560px,92vw);display:-webkit-box;overflow:hidden;margin:14px 0 6px;font-size:clamp(22px,5.5vw,34px);line-height:1.18;-webkit-box-orient:vertical;-webkit-line-clamp:2; }
.discover-live-card-copy > p { margin:0;color:rgba(255,255,255,.68);font-size:11px; }
.discover-live-card-enter { display:flex;align-items:center;gap:8px;margin-top:18px;padding:10px 17px;border:1px solid rgba(255,255,255,.72);border-radius:999px;color:#08090d;background:#fff;box-shadow:0 12px 30px rgba(0,0,0,.3);font-size:11px;font-weight:900;text-shadow:none; }
.discover-live-card-close { position:fixed;z-index:3013;top:calc(env(safe-area-inset-top) + 13px);inset-inline-start:14px;width:40px;height:40px;display:grid!important;place-items:center;border:1px solid rgba(255,255,255,.2)!important;border-radius:50%;background:rgba(8,9,13,.52)!important;box-shadow:0 8px 24px rgba(0,0,0,.25);font-size:17px!important;backdrop-filter:blur(14px); }
.discover-live-transition-nav { position:fixed;z-index:3012;top:calc(env(safe-area-inset-top) + 67px);inset-inline:12px;display:grid;grid-template-columns:minmax(0,1fr) auto minmax(0,1fr);align-items:center;gap:8px;pointer-events:none; }
.discover-live-transition-nav button { min-width:0;min-height:38px;display:flex;align-items:center;justify-content:center;gap:6px;padding:8px 11px;border:1px solid rgba(255,255,255,.22);border-radius:20px;color:#fff;background:rgba(8,9,13,.58);box-shadow:0 8px 24px rgba(0,0,0,.25);font-size:10px;font-weight:800;backdrop-filter:blur(14px);pointer-events:auto; }
.discover-live-transition-nav button span { overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
.discover-live-transition-nav button:disabled { opacity:.28; }
.discover-live-transition-nav > em { display:flex;align-items:center;gap:6px;padding:7px 9px;border-radius:16px;color:#fff;background:rgba(255,49,88,.82);font-size:8px;font-style:normal;font-weight:900;letter-spacing:.5px;box-shadow:0 7px 22px rgba(255,49,88,.24); }
.discover-live-transition-nav > em i { width:5px;height:5px;border-radius:50%;background:#fff;box-shadow:0 0 0 4px rgba(255,255,255,.18); }
[dir="rtl"] .discover-live-transition-nav button > i { transform:scaleX(-1); }
@keyframes discover-live-card-arrive { from { opacity:.45;transform:scale(1.08); } to { opacity:1;transform:scale(1.015); } }
@media (min-width:760px) { .discover-live-card-main::after { position:absolute;z-index:1;inset:12px;content:"";pointer-events:none;border:1px solid rgba(255,255,255,.13);border-radius:24px;box-shadow:inset 0 0 80px rgba(0,0,0,.18); }.discover-live-card-copy { inset-inline:clamp(34px,8vw,110px);bottom:clamp(45px,8vh,90px); } }
@media (prefers-reduced-motion:reduce) { .discover-live-card-backdrop,.discover-live-card-copy > em i { animation:none!important; } }
.book-hero { display:flex; gap:15px; padding:22px 16px; background:radial-gradient(circle at 20% 20%,rgba(53,228,212,.12),transparent 48%); }
.book-hero > img { width:112px; height:158px; flex:0 0 112px; border-radius:10px; object-fit:cover; box-shadow:0 18px 32px rgba(0,0,0,.4); }
.book-hero > div { min-width:0; padding-top:5px; }
.book-hero h1 { margin:10px 0 5px; font-size:21px; line-height:1.25; }
.book-hero strong { color:var(--cyan); font-size:10px; }
.book-hero p,.book-hero span { color:var(--muted); font-size:9px; line-height:1.6; }
.book-hero span { display:block; margin-top:9px; }
.book-description { padding:18px 16px; border-top:1px solid var(--line); border-bottom:1px solid var(--line); }
.book-description p { color:rgba(255,255,255,.68); font-size:10px; line-height:1.8; }
.book-description div { display:flex; gap:6px; }
.book-description span { padding:4px 7px; border-radius:10px; color:var(--cyan); background:rgba(53,228,212,.09); font-size:8px; }
.chapter-preview p { padding:13px; border-radius:10px; color:var(--muted); background:#12141a; font-size:9px; }
.novel-detail-overlay .overlay-scroll { height:calc(100% - 112px - env(safe-area-inset-top)); }
.novel-detail-actions { position:absolute; left:0; right:0; bottom:0; height:56px; display:grid; grid-template-columns:130px 1fr; gap:8px; padding:7px 12px calc(7px + env(safe-area-inset-bottom)); border-top:1px solid var(--line); background:rgba(8,9,13,.96); }
.novel-detail-actions button { border-radius:20px; border:1px solid var(--line); font-size:10px; }
.novel-detail-actions button:last-child { color:#08090d; background:#fff; font-weight:900; }
.chapter-row { width:100%; min-height:64px; display:flex; align-items:center; justify-content:space-between; padding:10px 16px; border-bottom:1px solid var(--line); text-align:left; }
.chapter-row span { min-width:0; }
.chapter-row small { display:block; color:var(--muted); font-size:8px; }
.chapter-row strong { display:block; overflow:hidden; margin-top:4px; font-size:11px; white-space:nowrap; text-overflow:ellipsis; }
.chapter-row > i { color:var(--muted); }
.chapter-row.active { border-inline-start:3px solid var(--coral); background:rgba(255,77,103,.08); }
.chapter-row.active small { color:var(--coral); }
.chapter-load-more { display:block; width:calc(100% - 32px); margin:14px auto 24px; padding:11px; border-radius:20px; color:var(--text); background:var(--panel-soft); font-size:10px; font-weight:800; }
.chapter-load-more:disabled { color:var(--muted); opacity:.7; }
.novel-detail-actions button:last-child:disabled { color:var(--muted); background:var(--panel-soft); cursor:not-allowed; }
.novel-reader { --reader-background:#f2eadb; --reader-text:#2e2923; --reader-muted:#897d70; --reader-border:rgba(64,49,34,.13); --reader-panel:rgba(247,240,226,.96); --reader-accent:#8c5934; position:absolute; z-index:70; inset:0; color:var(--reader-text); background:var(--reader-background); font-family:Georgia,"STSong","SimSun",serif; transition:color .25s ease,background .25s ease; }
.reader-theme-eye { --reader-background:#dfe7d3; --reader-text:#273229; --reader-muted:#687666; --reader-border:rgba(48,70,50,.14); --reader-panel:rgba(232,239,222,.96); --reader-accent:#58704f; }
.reader-theme-night { --reader-background:#171a20; --reader-text:#c9cbd1; --reader-muted:#777d88; --reader-border:rgba(255,255,255,.09); --reader-panel:rgba(29,33,40,.97); --reader-accent:#d69472; }
.novel-reader > header { position:relative;z-index:9;height:58px;display:grid;grid-template-columns:44px minmax(0,1fr) auto;align-items:center;padding-top:env(safe-area-inset-top);border-bottom:1px solid var(--reader-border);background:var(--reader-panel);backdrop-filter:blur(18px); }
.novel-reader > header > button,.reader-actions button { width:44px;height:44px;color:var(--reader-text);font-size:17px; }
.reader-heading { min-width:0;text-align:center; }
.reader-heading small,.reader-heading strong { display:block;overflow:hidden;white-space:nowrap;text-overflow:ellipsis; }
.reader-heading small { color:var(--reader-muted);font-size:8px; }
.reader-heading strong { margin-top:3px;font-size:10px; }
.reader-actions { display:flex;align-items:center; }
.reader-actions button.active { color:var(--reader-accent); }
.reader-actions button:disabled { opacity:.28;cursor:not-allowed; }
.audiobook-panel { position:absolute;z-index:12;inset-inline:14px;top:calc(70px + env(safe-area-inset-top));padding:14px 15px 16px;border:1px solid var(--reader-border);border-radius:17px;background:var(--reader-panel);box-shadow:0 18px 48px rgba(31,24,17,.2);backdrop-filter:blur(20px); }
.audiobook-panel > header { display:flex;align-items:center;justify-content:space-between;gap:12px; }
.audiobook-panel > header span { min-width:0; }
.audiobook-panel > header small,.audiobook-panel > header strong { display:block;overflow:hidden;white-space:nowrap;text-overflow:ellipsis; }
.audiobook-panel > header small { color:var(--reader-accent);font-family:"Bahnschrift","Microsoft YaHei UI",sans-serif;font-size:7px;font-weight:800;letter-spacing:1.6px; }
.audiobook-panel > header strong { margin-top:4px;font-size:12px; }
.audiobook-panel > header button { width:34px;height:34px;flex:0 0 34px;color:var(--reader-muted); }
.audiobook-panel > input[type="range"] { width:100%;height:4px;margin:16px 0 0;border:0;border-radius:4px;outline:0;background:linear-gradient(to right,var(--reader-accent) var(--audio-progress),var(--reader-border) var(--audio-progress));appearance:none; }
.audiobook-panel > input[type="range"]::-webkit-slider-thumb { width:16px;height:16px;border:3px solid var(--reader-panel);border-radius:50%;background:var(--reader-accent);box-shadow:0 2px 7px rgba(0,0,0,.25);appearance:none; }
.audiobook-times { display:flex;justify-content:space-between;margin-top:7px;color:var(--reader-muted);font-family:"Bahnschrift","Microsoft YaHei UI",sans-serif;font-size:8px; }
.audiobook-controls { display:grid;grid-template-columns:42px 52px 42px minmax(52px,auto);justify-content:center;gap:8px;align-items:center;margin-top:9px; }
.audiobook-controls button { height:42px;border:1px solid var(--reader-border);border-radius:50%;color:var(--reader-text);background:rgba(127,127,127,.06); }
.audiobook-controls button:disabled { opacity:.25;cursor:not-allowed; }
.audiobook-controls .audiobook-play { width:52px;height:52px;border:0;color:var(--reader-background);background:var(--reader-text);font-size:18px; }
.audiobook-controls .audiobook-rate { min-width:52px;padding-inline:8px;border-radius:22px;color:var(--reader-accent);font-family:"Bahnschrift","Microsoft YaHei UI",sans-serif;font-size:10px;font-weight:800; }
.reader-scroll { height:calc(100% - 58px - env(safe-area-inset-top)); overflow-y:auto; padding:0 24px 70px; }
.reader-scroll article { max-width:520px; margin:0 auto; }
.reader-scroll h1 { margin:35px 0 8px; font-size:24px; line-height:1.4; }
.reader-scroll article > small { color:var(--reader-muted); font-size:10px; }
.reader-content { margin-top:24px; overflow-wrap:anywhere; }
.reader-content p { margin:24px 0; font-size:var(--reader-font-size); line-height:var(--reader-line-height); text-align:justify; text-indent:2em; }
.reader-content h2,.reader-content h3 { margin:34px 0 14px; color:var(--reader-text); line-height:1.55; text-indent:0; }
.reader-content h2 { font-size:22px; }
.reader-content h3 { font-size:19px; }
.reader-content blockquote { margin:24px 0;padding:14px 16px;border-inline-start:3px solid var(--reader-accent);border-radius:0 9px 9px 0;background:rgba(115,81,51,.08);color:var(--reader-muted);font-size:calc(var(--reader-font-size) - 2px);line-height:var(--reader-line-height); }
.reader-content ul,.reader-content ol { margin:22px 0;padding-inline-start:28px;font-size:var(--reader-font-size);line-height:var(--reader-line-height); }
.reader-content li { margin:8px 0; }
.reader-content img { display:block;max-width:100%;height:auto;margin:24px auto;border-radius:12px; }
.reader-content a { color:var(--reader-accent);text-decoration:underline;text-underline-offset:3px; }
.reader-content hr { height:1px;margin:32px 12%;border:0;background:var(--reader-border); }
.reader-scroll footer { display:grid; grid-template-columns:repeat(2,1fr); gap:12px; margin-top:40px; }
.reader-scroll footer button { padding:12px; border:1px solid var(--reader-border); border-radius:22px; color:var(--reader-text); }
.reader-scroll footer button:disabled { opacity:.3; }
.reader-progress { position:absolute; right:10px; bottom:10px; padding:4px 7px; border:1px solid var(--reader-border); border-radius:10px; color:var(--reader-muted); background:var(--reader-panel); font-size:8px; }
[dir="rtl"] .reader-progress { right:auto;left:10px; }
[dir="rtl"] .novel-reader > header > button:first-child i,[dir="rtl"] .directory-overlay .overlay-header > button:first-child i { transform:rotate(180deg); }
.ranking-list { padding:8px 16px 30px; }
.ranking-list > button { width:100%; display:grid; grid-template-columns:30px 58px 1fr 20px; gap:10px; align-items:center; padding:10px 0; border-bottom:1px solid var(--line); text-align:left; }
.ranking-list > button > b { color:var(--coral); font-family:Georgia,serif; font-size:20px; font-style:italic; text-align:center; }
.ranking-list img { width:58px; height:78px; border-radius:7px; object-fit:cover; }
.ranking-list span { min-width:0; }
.ranking-list strong { font-size:11px; }
.ranking-list p { display:-webkit-box; overflow:hidden; margin:5px 0; color:var(--muted); font-size:8px; line-height:1.45; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
.ranking-list em { color:rgba(255,255,255,.4); font-size:8px; font-style:normal; }
.ranking-list > button > i { color:var(--muted); }
.music-rail { display:flex; gap:11px; overflow-x:auto; padding:0 16px 4px; scrollbar-width:none; }
.music-rail::-webkit-scrollbar { display:none; }
.music-rail > button { width:118px; flex:0 0 118px; text-align:left; }
.music-rail > button > span { position:relative; width:118px; height:118px; display:block; overflow:hidden; border-radius:50%; border:1px solid rgba(255,255,255,.12); box-shadow:0 12px 28px rgba(0,0,0,.32); }
.music-rail img { width:100%; height:100%; object-fit:cover; }
.music-rail span > i { position:absolute; left:50%; top:50%; width:30px; height:30px; display:grid; place-items:center; transform:translate(-50%,-50%); border-radius:50%; background:rgba(8,9,13,.72); backdrop-filter:blur(8px); }
.music-rail span > em { position:absolute; right:4px; bottom:7px; padding:3px 6px; border-radius:8px; color:#fff; background:var(--coral); font-size:7px; font-style:normal; }
.music-rail strong,.music-rail small { display:block; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.music-rail strong { margin-top:9px; font-size:10px; }
.music-rail small { margin-top:3px; color:var(--muted); font-size:8px; }
.music-feature { position:relative; height:240px; overflow:hidden; margin:8px 16px 22px; border-radius:18px; background:#11151d; box-shadow:0 20px 50px rgba(0,0,0,.35); }
.music-feature > img { width:100%; height:100%; object-fit:cover; }
.music-feature-shade { position:absolute; inset:0; background:linear-gradient(90deg,rgba(4,6,10,.9),rgba(4,6,10,.46) 58%,rgba(4,6,10,.14)),linear-gradient(0deg,rgba(4,6,10,.74),transparent 55%); }
.music-feature > div { position:absolute; z-index:2; left:20px; right:20px; bottom:18px; }
.music-feature small { color:var(--cyan); font-size:7px; font-weight:900; letter-spacing:1.6px; }
.music-feature h2 { max-width:70%; margin:8px 0 4px; font-size:24px; line-height:1.08; }
.music-feature strong { color:rgba(255,255,255,.78); font-size:9px; }
.music-feature p { display:-webkit-box; max-width:72%; overflow:hidden; margin:8px 0 12px; color:rgba(255,255,255,.65); font-size:8px; line-height:1.5; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
.music-feature button { padding:8px 13px; border-radius:18px; color:#08090d; background:#fff; font-size:9px; font-weight:900; }
.music-list { padding:0 16px; }
.music-row { min-height:70px; display:flex; align-items:center; border-bottom:1px solid var(--line); }
.music-main { min-width:0; flex:1; display:flex; align-items:center; gap:11px; padding:9px 0; text-align:left; }
.music-main > b { width:22px; flex:0 0 22px; color:rgba(255,255,255,.28); font-family:Georgia,serif; font-size:12px; font-style:italic; text-align:center; }
.music-cover { position:relative; width:48px; height:48px; flex:0 0 48px; overflow:hidden; border-radius:12px; background:#171a22; }
.music-cover img { width:100%; height:100%; object-fit:cover; }
.music-cover i { position:absolute; right:4px; bottom:4px; width:20px; height:20px; display:grid; place-items:center; border-radius:50%; background:rgba(8,9,13,.72); font-size:9px; }
.music-copy { min-width:0; display:block; }
.music-copy strong,.music-copy small,.music-copy em { display:block; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.music-copy strong { font-size:10px; }
.music-copy small { margin-top:4px; color:rgba(255,255,255,.58); font-size:8px; }
.music-copy em { margin-top:4px; color:rgba(255,255,255,.34); font-size:7px; font-style:normal; }
.music-favorite { width:42px; height:42px; flex:0 0 42px; color:rgba(255,255,255,.62); font-size:15px; }
.music-favorite .el-icon-star-on { color:var(--coral); }
.music-player { position:absolute; z-index:80; inset:0; overflow:hidden; color:#fff; background:#08090d; }
.music-player-backdrop { position:absolute; inset:-30px; overflow:hidden; }
.music-player-backdrop img { width:100%; height:100%; object-fit:cover; filter:blur(38px) saturate(.8); opacity:.28; transform:scale(1.18); }
.music-player-backdrop::after { content:""; position:absolute; inset:0; background:linear-gradient(180deg,rgba(6,8,13,.55),#08090d 82%); }
.music-player > header { position:relative; z-index:2; height:58px; display:grid; grid-template-columns:48px 1fr 48px; align-items:center; padding-top:env(safe-area-inset-top); }
.music-player > header strong { overflow:hidden; font-size:9px; letter-spacing:1.2px; text-align:center; text-transform:uppercase; white-space:nowrap; text-overflow:ellipsis; }
.music-player > header button { height:44px; font-size:17px; }
.music-player > main { position:relative; z-index:2; height:calc(100% - 58px - env(safe-area-inset-top)); display:flex; flex-direction:column; align-items:center; overflow:hidden; padding:12px 24px calc(16px + env(safe-area-inset-bottom)); }
.music-disc { position:relative; width:clamp(112px,35vw,150px); height:clamp(112px,35vw,150px); flex:0 0 auto; padding:9px; border-radius:50%; background:repeating-radial-gradient(circle at center,#151820 0 3px,#08090d 4px 7px); box-shadow:0 20px 52px rgba(0,0,0,.52); }
.music-disc.spinning { animation:musicSpin 12s linear infinite; }
.music-disc img { width:100%; height:100%; object-fit:cover; border-radius:50%; }
.music-disc span { position:absolute; left:50%; top:50%; width:32px; height:32px; transform:translate(-50%,-50%); border:8px solid #0b0d12; border-radius:50%; background:var(--cyan); }
.music-player-copy { width:100%; margin-top:13px; text-align:center; }
.music-player-copy small { color:var(--cyan); font-size:8px; letter-spacing:1px; text-transform:uppercase; }
.music-player-copy h1 { overflow:hidden; margin:7px 0 4px; font-size:23px; line-height:1.2; white-space:nowrap; text-overflow:ellipsis; }
.music-player-copy strong { color:rgba(255,255,255,.68); font-size:10px; }
.music-player-copy p { display:-webkit-box; overflow:hidden; margin:7px auto 0; color:rgba(255,255,255,.44); font-size:8px; line-height:1.5; -webkit-box-orient:vertical; -webkit-line-clamp:1; }
.music-lyrics { position:relative; width:100%; min-height:112px; flex:1 1 auto; overflow-y:auto; margin-top:10px; text-align:center; scrollbar-width:none; overscroll-behavior:contain; mask-image:linear-gradient(transparent,#000 19%,#000 81%,transparent); }
.music-lyrics::-webkit-scrollbar { display:none; }
.music-lyrics-track { padding:50px 0; }
.music-lyrics-track button { width:100%; display:block; padding:8px 6px; color:rgba(255,255,255,.36); font-size:11px; line-height:1.45; transition:color .24s ease,transform .28s ease,font-size .28s ease; overflow-wrap:anywhere; }
.music-lyrics-track button.passed { color:rgba(255,255,255,.22); }
.music-lyrics-track button.active { color:#fff; font-size:14px; font-weight:800; transform:scale(1.03); text-shadow:0 4px 18px rgba(53,228,212,.28); }
.music-lyrics-empty { min-height:100%; display:grid; place-content:center; justify-items:center; gap:8px; color:rgba(255,255,255,.38); font-size:10px; }
.music-lyrics-empty i { color:var(--cyan); font-size:22px; }
.music-progress { width:100%; display:grid; grid-template-columns:1fr 1fr; gap:4px; margin-top:7px; color:rgba(255,255,255,.48); font-size:8px; font-variant-numeric:tabular-nums; }
.music-progress input { --music-progress:0%; grid-column:1/-1; width:100%; height:16px; margin:0; appearance:none; background:transparent; }
.music-progress input::-webkit-slider-runnable-track { height:3px; border-radius:3px; background:linear-gradient(90deg,var(--cyan) 0 var(--music-progress),rgba(255,255,255,.2) var(--music-progress) 100%); }
.music-progress input::-webkit-slider-thumb { width:12px; height:12px; margin-top:-4.5px; appearance:none; border:2px solid #fff; border-radius:50%; background:var(--cyan); }
.music-progress span:last-child { text-align:right; }
.music-controls { width:100%; display:grid; grid-template-columns:40px 44px 66px 44px 40px; align-items:center; justify-content:space-between; margin-top:12px; }
.music-controls button { height:42px; font-size:17px; }
.music-controls button:disabled { opacity:.25; }
.music-controls .active { color:var(--cyan); }
.music-play-toggle { width:62px; height:62px !important; border-radius:50%; color:#08090d !important; background:#fff !important; font-size:25px !important; box-shadow:0 12px 32px rgba(0,0,0,.35); }
[dir="rtl"] .music-main,[dir="rtl"] .music-feature > div { text-align:right; }
@keyframes musicSpin { to { transform:rotate(360deg); } }
@media(max-height:680px){.music-player > main{padding-top:7px}.music-disc{width:96px;height:96px}.music-player-copy{margin-top:8px}.music-player-copy p{display:none}.music-lyrics{min-height:90px;margin-top:5px}.music-lyrics-track{padding:38px 0}.music-controls{margin-top:7px}.music-play-toggle{width:54px;height:54px!important}}
.game-rail { display:flex; gap:11px; overflow-x:auto; padding:0 16px 4px; scrollbar-width:none; }
.game-rail::-webkit-scrollbar { display:none; }
.game-rail > button { width:132px; flex:0 0 132px; text-align:left; }
.game-rail > button > span { position:relative; width:132px; height:176px; display:block; overflow:hidden; border-radius:16px; background:#171920; box-shadow:0 16px 30px rgba(0,0,0,.3); }
.game-rail img { width:100%; height:100%; object-fit:cover; }
.game-rail span > i,.game-grid .game-card-main > span > i { position:absolute; right:8px; bottom:8px; width:29px; height:29px; display:grid; place-items:center; border-radius:50%; color:#08090d; background:#fff; box-shadow:0 8px 18px rgba(0,0,0,.3); }
.game-rail span > em,.game-grid .game-card-main > span > em { position:absolute; left:8px; top:8px; padding:4px 7px; border-radius:9px; color:#fff; background:var(--coral); font-size:7px; font-style:normal; font-weight:900; }
.game-rail strong,.game-rail small { display:block; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.game-rail strong { margin-top:8px; font-size:10px; }
.game-rail small { margin-top:3px; color:var(--muted); font-size:8px; }
.game-feature { position:relative; height:245px; overflow:hidden; margin:8px 16px 22px; border-radius:19px; background:#11131b; box-shadow:0 22px 55px rgba(0,0,0,.42); }
.game-feature > img { width:100%; height:100%; object-fit:cover; }
.game-feature > span { position:absolute; inset:0; background:linear-gradient(90deg,rgba(5,7,13,.92),rgba(5,7,13,.48) 62%,transparent),linear-gradient(0deg,rgba(5,7,13,.65),transparent 65%); }
.game-feature > div { position:absolute; z-index:2; left:20px; right:20px; bottom:18px; }
.game-feature small { color:var(--cyan); font-size:7px; font-weight:900; letter-spacing:1.5px; }
.game-feature h2 { max-width:72%; margin:8px 0 5px; font-size:25px; line-height:1.1; }
.game-feature p { display:-webkit-box; max-width:76%; overflow:hidden; margin:0 0 7px; color:rgba(255,255,255,.62); font-size:8px; line-height:1.5; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
.game-feature em { display:block; color:rgba(255,255,255,.44); font-size:8px; font-style:normal; }
.game-feature button { margin-top:11px; padding:9px 15px; border-radius:18px; color:#08090d; background:#fff; font-size:9px; font-weight:900; }
.game-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px 11px; padding:0 16px; }
.game-grid > article { position:relative; min-width:0; }
.game-card-main { width:100%; min-width:0; padding:0; text-align:left; }
.game-card-main > span { position:relative; height:205px; display:block; overflow:hidden; border-radius:15px; background:#171920; box-shadow:0 14px 28px rgba(0,0,0,.24); }
.game-card-main img { width:100%; height:100%; object-fit:cover; }
.game-card-main > strong,.game-card-main > small { display:block; overflow:hidden; padding-right:30px; white-space:nowrap; text-overflow:ellipsis; }
.game-card-main > strong { margin-top:8px; font-size:11px; }
.game-card-main > small { margin-top:4px; color:var(--muted); font-size:8px; }
.game-card-favorite { position:absolute; right:-4px; bottom:-4px; width:36px; height:36px; display:grid; place-items:center; color:rgba(255,255,255,.58); font-size:14px; }
.game-card-favorite .el-icon-star-on { color:var(--coral); }
.search-game-grid { padding:0; }
[dir="rtl"] .game-rail > button,[dir="rtl"] .game-card-main,[dir="rtl"] .game-feature > div { text-align:right; }
[dir="rtl"] .game-card-main > strong,[dir="rtl"] .game-card-main > small { padding-right:0; padding-left:30px; }
[dir="rtl"] .game-card-favorite { right:auto; left:-4px; }
.discover-panel-enter-active,.discover-panel-leave-active { transition:transform .22s ease,opacity .22s ease; }
.discover-panel-enter,.discover-panel-leave-to { opacity:.5; transform:translateX(100%); }
.discover-fade-enter-active,.discover-fade-leave-active { transition:opacity .2s ease; }
.discover-fade-enter,.discover-fade-leave-to { opacity:0; }
@keyframes skeletonMove { to { background-position:-200% 0; } }
@media (prefers-reduced-motion:reduce) {
  .discover-channel-arrow { transition:none; }
}
@media (max-width:360px) {
  .drama-grid > button > span { height:184px; }
  .game-card-main > span { height:184px; }
  .poster-rail > button { flex-basis:108px; }
  .poster { height:145px; }
}
.discover-page { bottom:var(--bottom-nav-height); }
</style>
